import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { API_BASE, uploadsPublicUrl } from '../config/api';
import TemplateJsonExportModal from '../components/TemplateJsonExportModal';
import LeadloversIntegrationPanel from '../components/LeadloversIntegrationPanel';
import { normalizeLayoutSchemaForJsonExport } from '../utils/templateSchemaExport';
import {
    ALL_ELEMENTS,
    ALL_GRIDS,
    ALL_MODULES,
    ELEMENT_TIER_ORDER,
    categoriesUpToMaxTier,
    deriveElementCategoryStateFromPlan,
    getMergedElementTiers,
    getMergedGridTiers,
    getMergedModuleTiers,
    getPlanSaveSlotLimit,
    resolveMaxElementTier,
    resolvePlanAllowedElements,
} from '../config/planFeatures';
const API = API_BASE;

const getToken = () => localStorage.getItem('gepetto_token') || sessionStorage.getItem('gepetto_token');
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

/** Editor rico (termos / privacidade): mesma família de ferramentas do builder. */
const LEGAL_QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
    ],
    clipboard: { matchVisual: false },
};

// ─── KPI CARD ────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color = '#3b82f6', icon }) => (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border-l-4 flex items-center gap-4" style={{ borderLeftColor: color }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: color + '18' }}>{icon}</div>
        <div>
            <div className="text-3xl font-extrabold text-slate-800 dark:text-slate-200 leading-none">{value ?? '—'}</div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{label}</div>
            {sub && <div className="text-xs font-semibold mt-0.5" style={{ color }}>{sub}</div>}
        </div>
    </div>
);

// ─── STATUS BADGE ─────────────────────────────────────────────
const Badge = ({ status }) => {
    const { t } = useTranslation(['master']);
    const map = {
        active: ['#10b981', '#d1fae5', t('master:statusActive')],
        paid: ['#10b981', '#d1fae5', t('master:orderStatusPaid')],
        trial: ['#f59e0b', '#fef3c7', t('master:statusTrial')],
        pending: ['#6366f1', '#e0e7ff', t('master:statusPending')],
        pending_checkout: ['#6366f1', '#e0e7ff', t('master:orderStatusPendingCheckout')],
        overdue: ['#ef4444', '#fee2e2', t('master:statusOverdue')],
        suspended: ['#6b7280', '#f3f4f6', t('master:statusSuspended')],
        cancelled: ['#dc2626', '#fee2e2', t('master:statusCancelled')],
    };
    const [color, bg, label] = map[status] || ['#64748b', '#f1f5f9', status];
    return <span style={{ background: bg, color, borderRadius: '999px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>{label}</span>;
};

// ─── DASHBOARD ────────────────────────────────────────────────
const fmtOrderMoney = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};

const DashboardView = ({ t }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/master/reports`, { headers: authHeaders() })
            .then(r => r.json()).then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-8 text-slate-500 dark:text-slate-400">⏳ {t ? t('master:loading') : 'Carregando...'}</div>;

    const k = data?.kpis || {};
    const mrr = parseFloat(k.mrr || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return (
        <div>
            <h2 className="m-0 mb-6 text-slate-800 dark:text-slate-200">📊 {t ? t('master:dashboard') : 'Dashboard'}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <KpiCard label={t ? t('master:totalClients') : 'Total de Clientes'} value={k.total_tenants} icon="🏢" color="#3b82f6" />
                <KpiCard label={t ? t('master:activeClients') : 'Clientes Ativos'} value={k.active_tenants} icon="✅" color="#10b981" />
                <KpiCard label={t ? t('master:trialClients') : 'Em Trial'} value={k.trial_tenants} icon="⏳" color="#f59e0b" />
                <KpiCard label={t ? t('master:pendingClients') : 'Pendentes'} value={k.pending_tenants ?? 0} icon="🕐" color="#6366f1" />
                <KpiCard label={t ? t('master:mrr') : 'MRR'} value={mrr} icon="💰" color="#0ea5e9" sub={t ? t('master:mrrSub') : 'Receita Mensal Recorrente'} />
            </div>

            <h3 className="text-slate-800 dark:text-slate-200 mb-4">🧾 {t ? t('master:recentOrders') : 'Últimos pedidos'}</h3>
            <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 mb-8">
                {(data?.recentOrders || []).length === 0 ? (
                    <div className="p-8 text-center text-slate-400 dark:text-slate-500">{t ? t('master:noRecentOrders') : 'Nenhum pedido registrado ainda.'}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50">
                                    <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">{t ? t('master:orderDate') : 'Data'}</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">{t ? t('master:orderClient') : 'Cliente'}</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">{t ? t('master:orderProduct') : 'Produto'}</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">{t ? t('master:orderAmount') : 'Valor'}</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">{t ? t('master:orderPaymentStatus') : 'Status pagamento'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.recentOrders || []).map((row) => (
                                    <tr key={`${row.payment_kind}-${row.order_id}`} className="border-b border-slate-100 dark:border-slate-700/80">
                                        <td className="px-4 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                            {row.order_at ? new Date(row.order_at).toLocaleString('pt-BR') : '—'}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-semibold text-slate-800 dark:text-slate-200">{row.tenant_name || '—'}</div>
                                            {row.slug && <div className="text-xs text-slate-400">{row.slug}</div>}
                                        </td>
                                        <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{row.product_label || '—'}</td>
                                        <td className="px-4 py-2 whitespace-nowrap font-semibold text-slate-700 dark:text-slate-300">{fmtOrderMoney(row.amount_brl)}</td>
                                        <td className="px-4 py-2"><Badge status={row.payment_status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <h3 className="text-slate-800 dark:text-slate-200 mb-4">📋 {t ? t('master:recentEvents') : 'Eventos Recentes'}</h3>
            <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm">
                {(data?.recentEvents || []).length === 0
                    ? <div className="p-8 text-center text-slate-400 dark:text-slate-500">{t ? t('master:noEvents') : 'Nenhum evento registrado ainda.'}</div>
                    : (data.recentEvents || []).map(e => (
                        <div key={e.id} className="p-3 px-5 border-b border-slate-100 dark:border-slate-700 flex gap-4 items-center">
                            <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString('pt-BR')}</span>
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{e.event_type}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 flex-1">{t ? t('master:tenant') : 'Tenant'}: {e.tenant_id || 'N/A'}</span>
                        </div>
                    ))}
            </div>
        </div>
    );
};

// ─── MODAL: Chave API gerada (exibir uma vez) ──────────────────
const ApiKeyModal = ({ apiKey, onClose }) => (
    <div className="fixed inset-0 bg-black/60 z-[1100] flex items-center justify-center p-6">
        <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl border border-slate-200 dark:border-slate-700">
            <h3 className="m-0 mb-2 text-lg font-semibold text-slate-800 dark:text-slate-200">🔑 Chave de API gerada</h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                Copie agora. Esta chave não será exibida novamente.
            </p>
            <div className="flex gap-2 mb-4">
                <input type="text" readOnly value={apiKey} className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-mono bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                <button type="button" onClick={() => { navigator.clipboard.writeText(apiKey); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold cursor-pointer hover:bg-blue-700">
                    Copiar
                </button>
            </div>
            <button type="button" onClick={onClose} className="w-full py-2 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg font-semibold cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-500">
                Fechar
            </button>
        </div>
    </div>
);

// ─── MODAL DE EDIÇÃO DE CLIENTE ───────────────────────────────
function moodleExpiresToDatetimeLocal(value) {
    if (value == null || value === '') return '';
    const s = String(value).replace(' ', 'T');
    return s.length >= 16 ? s.slice(0, 16) : s;
}

const EditTenantModal = ({ tenant, plans, onClose, onSaved }) => {
    const { t } = useTranslation(['master']);
    const [form, setForm] = useState({
        name: tenant.name || '',
        slug: tenant.slug || '',
        ownerName: tenant.owner_name || '',
        ownerEmail: tenant.owner_email || '',
        planId: tenant.current_plan_id || 'plan_free',
        status: tenant.status || 'pending',
        moodle_domain: tenant.moodle_domain || '',
        api_suspended: !!tenant.api_suspended,
        moodle_api_expires_at: moodleExpiresToDatetimeLocal(tenant.moodle_api_expires_at),
        ownerAiLibraryCurator: !!Number(tenant.owner_ai_library_curator),
    });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    /** Se false, ao salvar busca nome/e-mail atuais do banco para não regravar valores velhos do modal ao mudar só o plano. */
    const [ownerTouched, setOwnerTouched] = useState(false);
    const [generatedKey, setGeneratedKey] = useState(null);
    const [generatingKey, setGeneratingKey] = useState(false);
    const [copyingKey, setCopyingKey] = useState(false);
    const [clearingPlugin, setClearingPlugin] = useState(false);

    // Ao abrir o modal, carrega domínio salvo da API para sempre exibir o valor do banco
    useEffect(() => {
        setOwnerTouched(false);
        setForm(prev => ({
            ...prev,
            moodle_domain: tenant.moodle_domain ?? prev.moodle_domain ?? '',
            api_suspended: !!tenant.api_suspended,
            moodle_api_expires_at: moodleExpiresToDatetimeLocal(tenant.moodle_api_expires_at),
            ownerAiLibraryCurator: !!Number(tenant.owner_ai_library_curator),
        }));
        fetch(`${API}/master/tenants/${tenant.id}/domains`, { headers: authHeaders() })
            .then(r => r.json())
            .then(d => { if (d.domains && d.domains[0]) setForm(prev => ({ ...prev, moodle_domain: d.domains[0] })); })
            .catch(() => {});
    }, [tenant.id, tenant.moodle_domain, tenant.api_suspended, tenant.moodle_api_expires_at, tenant.owner_ai_library_curator]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true); setMsg('');
        try {
            let ownerName = form.ownerName;
            let ownerEmail = form.ownerEmail;
            if (!ownerTouched) {
                try {
                    const lr = await fetch(`${API}/master/tenants`, { headers: authHeaders() });
                    const ld = await lr.json();
                    const row = (ld.tenants || []).find((x) => x.id === tenant.id);
                    if (row) {
                        if (row.owner_name != null && String(row.owner_name).trim()) ownerName = String(row.owner_name).trim();
                        if (row.owner_email != null && String(row.owner_email).trim()) ownerEmail = String(row.owner_email).trim();
                    }
                } catch (_) { /* mantém form */ }
            }
            const r = await fetch(`${API}/master/tenants/${tenant.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    name: form.name,
                    slug: form.slug,
                    ownerName,
                    ownerEmail,
                    planId: form.planId,
                    status: form.status,
                    moodle_domain: form.moodle_domain.trim() || undefined,
                    api_suspended: form.api_suspended,
                    moodle_api_expires_at: form.moodle_api_expires_at.trim()
                        ? new Date(form.moodle_api_expires_at).toISOString()
                        : null,
                    ownerAiLibraryCurator: !!form.ownerAiLibraryCurator,
                })
            });
            const d = await r.json();
            if (!r.ok) { setMsg('❌ ' + d.error); setSaving(false); return; }
            setMsg('✅ Cliente atualizado!');
            setTimeout(() => { onSaved(); onClose(); }, 600);
        } catch { setMsg('❌ Erro de rede.'); }
        setSaving(false);
    };

    const handleGenerateApiKey = async () => {
        const domain = form.moodle_domain.trim();
        if (!domain) {
            setMsg('Informe o domínio antes de gerar a chave de API.');
            return;
        }
        setGeneratingKey(true); setMsg('');
        try {
            // Salva o domínio antes de gerar a chave (para não depender de ter clicado em Salvar)
            const patchRes = await fetch(`${API}/master/tenants/${tenant.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ moodle_domain: domain, api_suspended: form.api_suspended }),
            });
            if (!patchRes.ok) {
                const patchData = await patchRes.json().catch(() => ({}));
                setMsg('❌ ' + (patchData.error || 'Erro ao salvar domínio.'));
                setGeneratingKey(false);
                return;
            }
            const r = await fetch(`${API}/master/tenants/${tenant.id}/api-key`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ domain }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                setGeneratedKey(d.api_key);
                onSaved();
            } else {
                const errMsg = d.error || 'Erro ao gerar chave';
                setMsg(r.status === 404
                    ? `❌ ${errMsg} Confira se o backend está atualizado e reinicie-o (pm2 restart ou cPanel).`
                    : '❌ ' + errMsg);
            }
        } catch { setMsg('❌ Erro de rede. Verifique se a API está online.'); }
        setGeneratingKey(false);
    };

    const handleCopyApiKey = async () => {
        if (!tenant.api_key_prefix) { setMsg('Gere uma chave primeiro.'); return; }
        setCopyingKey(true); setMsg('');
        try {
            const r = await fetch(`${API}/master/tenants/${tenant.id}/api-key`, { headers: authHeaders() });
            const d = await r.json().catch(() => ({}));
            if (r.ok && d.api_key) {
                await navigator.clipboard.writeText(d.api_key);
                setMsg('✅ Chave copiada para a área de transferência.');
            } else setMsg('❌ ' + (d.error || 'Chave não armazenada. Use "Regenerar chave" para gerar e salvar uma nova.'));
        } catch { setMsg('❌ Erro ao copiar.'); }
        setCopyingKey(false);
    };

    const handleClearMoodlePlugin = async () => {
        if (!window.confirm(t('master:moodlePluginClearConfirm'))) return;
        setClearingPlugin(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/tenants/${tenant.id}/moodle-plugin/clear`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) {
                setMsg('❌ ' + (d.error || t('master:networkError')));
            } else {
                setMsg('✅ ' + t('master:moodlePluginCleared'));
                onSaved();
            }
        } catch {
            setMsg('❌ ' + t('master:networkError'));
        }
        setClearingPlugin(false);
    };

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-6">
                <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-xl">
                    <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                        <h3 className="m-0 text-lg font-semibold text-slate-800 dark:text-slate-200">✏️ {t('master:editClient')}</h3>
                        <button type="button" onClick={onClose} className="bg-transparent border-none text-2xl cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">&times;</button>
                    </div>

                    {msg && <div className={`mx-6 mt-4 p-3 rounded-lg font-semibold ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div>}

                    <form onSubmit={handleSave} className="p-6">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className={labelCls}>{t('master:companyName')}</label>
                                <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:slug')}</label>
                                <input required value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:adminName')}</label>
                                <input value={form.ownerName} onChange={e => { setOwnerTouched(true); setForm(p => ({ ...p, ownerName: e.target.value })); }} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:adminEmail')}</label>
                                <input type="email" value={form.ownerEmail} onChange={e => { setOwnerTouched(true); setForm(p => ({ ...p, ownerEmail: e.target.value })); }} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:plan')}</label>
                                <select value={form.planId} onChange={e => setForm(p => ({ ...p, planId: e.target.value }))} className={inputCls}>
                                    {plans.map(pl => <option key={pl.id} value={pl.id}>{pl.name} — R$ {parseFloat(pl.price_brl).toFixed(2)}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:status')}</label>
                                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className={inputCls}>
                                    <option value="active">{t('master:statusActive')}</option>
                                    <option value="trial">{t('master:statusTrial')}</option>
                                    <option value="pending">{t('master:statusPending')}</option>
                                    <option value="suspended">{t('master:statusSuspended')}</option>
                                    <option value="cancelled">{t('master:statusCancelled')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg mb-4">
                            ⚠️ Subdomínio: <strong>{form.slug || 'slug'}.geppetto-creator.com</strong>
                        </div>

                        {/* Integração Moodle: domínio, chave API, suspender API */}
                        <div className="mb-4 p-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50">
                            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">🔌 Integração Moodle (API)</h4>
                            <div className="space-y-3">
                                {tenant.moodle_plugin_base_url && (
                                    <div>
                                        <label className={labelCls}>{t('master:moodlePluginLockedUrl')}</label>
                                        <div className="text-xs font-mono break-all px-2.5 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                                            {tenant.moodle_plugin_base_url}
                                        </div>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{t('master:moodlePluginLockedUrlHint')}</p>
                                    </div>
                                )}
                                <div>
                                    <label className={labelCls}>{t('master:moodleApiExpiresAt')}</label>
                                    <input
                                        type="datetime-local"
                                        value={form.moodle_api_expires_at}
                                        onChange={e => setForm(p => ({ ...p, moodle_api_expires_at: e.target.value }))}
                                        className={inputCls}
                                    />
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{t('master:moodleApiExpiresAtHint')}</p>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        onClick={handleClearMoodlePlugin}
                                        disabled={clearingPlugin}
                                        className="px-3 py-2 text-xs font-semibold rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60"
                                    >
                                        {clearingPlugin ? '…' : t('master:moodlePluginClear')}
                                    </button>
                                </div>
                                <div>
                                    <label className={labelCls}>Domínio Moodle (pode trocar e salvar)</label>
                                    <input value={form.moodle_domain} onChange={e => setForm(p => ({ ...p, moodle_domain: e.target.value }))} className={inputCls} placeholder="ex: moodle.empresa.com" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" id="api_suspended" checked={form.api_suspended} onChange={e => setForm(p => ({ ...p, api_suspended: e.target.checked }))} className="rounded border-slate-300" />
                                    <label htmlFor="api_suspended" className="text-sm text-slate-700 dark:text-slate-300">API suspensa (desliga só a integração via API; demais serviços continuam normais)</label>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {tenant.api_key_prefix && (
                                        <>
                                            <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">Chave: {tenant.api_key_prefix}…</span>
                                            <button type="button" onClick={handleCopyApiKey} disabled={copyingKey} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg text-xs font-semibold cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-500 disabled:opacity-60">
                                                {copyingKey ? 'Copiando...' : 'Copiar chave'}
                                            </button>
                                        </>
                                    )}
                                    <button type="button" onClick={handleGenerateApiKey} disabled={generatingKey} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold cursor-pointer hover:bg-blue-700 disabled:opacity-60">
                                        {generatingKey ? 'Gerando...' : tenant.api_key_prefix ? 'Regenerar chave' : 'Gerar chave de API'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4 p-4 rounded-lg border border-violet-200 dark:border-violet-800/60 bg-violet-50/80 dark:bg-violet-950/30">
                            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Biblioteca IA (conta Mestra)</h4>
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
                                Marca o admin deste cliente como curador da biblioteca Global de seções (metadados para montagem por IA). Não altera o papel super_admin.
                            </p>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="owner_ai_library_curator"
                                    checked={!!form.ownerAiLibraryCurator}
                                    onChange={e => setForm(p => ({ ...p, ownerAiLibraryCurator: e.target.checked }))}
                                    className="rounded border-slate-300"
                                />
                                <label htmlFor="owner_ai_library_curator" className="text-sm text-slate-700 dark:text-slate-300">
                                    Conta Mestra — curadoria da biblioteca IA
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4">
                            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 cursor-pointer font-semibold text-slate-800 dark:text-slate-200">{t('master:cancel')}</button>
                            <button type="submit" disabled={saving} className="px-6 py-2 border-none rounded-lg bg-blue-600 text-white cursor-pointer font-bold disabled:opacity-60">
                                {saving ? '...' : t('master:save')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {generatedKey && <ApiKeyModal apiKey={generatedKey} onClose={() => { setGeneratedKey(null); onSaved(); }} />}
        </>
    );
};

// ─── CLIENTES ─────────────────────────────────────────────────
const tenantSignupDate = (tenant) => {
    const raw = tenant?.created_at || tenant?.registered_at || tenant?.createdAt || tenant?.created;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
};

const tenantSignupDateInput = (tenant) => {
    const d = tenantSignupDate(tenant);
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtTenantApiUrl = (tenant) => {
    const url = tenant?.moodle_plugin_base_url;
    return (url && String(url).trim()) ? String(url).trim() : 'vazio';
};

const ClientsView = ({ plans, onLoginAsTenant }) => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterPlan, setFilterPlan] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [form, setForm] = useState({ name: '', slug: '', ownerName: '', ownerEmail: '', ownerPassword: '', planId: 'plan_free' });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${API}/master/tenants`, { headers: authHeaders() })
            .then(r => r.json()).then(d => { setTenants(d.tenants || []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // Quando a lista de clientes atualiza (ex.: após salvar/gerar chave), atualiza o cliente em edição para o modal mostrar domínio e chave
    useEffect(() => {
        if (editingTenant && tenants.length) {
            const updated = tenants.find(t => t.id === editingTenant.id);
            if (updated) setEditingTenant(updated);
        }
    }, [tenants]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true); setMsg('');
        try {
            const r = await fetch(`${API}/master/tenants`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
            const d = await r.json();
            if (!r.ok) { setMsg('❌ ' + d.error); setSaving(false); return; }
            setMsg('✅ ' + d.message);
            setShowForm(false);
            setForm({ name: '', slug: '', ownerName: '', ownerEmail: '', ownerPassword: '', planId: 'plan_free' });
            load();
        } catch { setMsg('❌ Erro de rede.'); }
        setSaving(false);
    };

    const toggleStatus = async (t) => {
        const newStatus = (t.status === 'active' ? 'suspended' : 'active');
        await fetch(`${API}/master/tenants/${t.id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status: newStatus }) });
        load();
    };

    const handleAccess = async (t) => {
        try {
            const r = await fetch(`${API}/master/tenants/${t.id}/login`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (r.ok && d.token) {
                const qs = new URLSearchParams({ tenant_token: d.token });
                if (d.sessionId) qs.set('session_id', d.sessionId);
                const url = `${window.location.origin}?${qs.toString()}`;
                window.open(url, '_blank');
            } else {
                alert('❌ ' + (d.error || 'Erro ao acessar painel.'));
            }
        } catch { alert('❌ Erro de conexão.'); }
    };

    const handleDelete = async (t) => {
        if (!confirm(`Tem certeza que deseja EXCLUIR o cliente "${t.name}"?\n\nIsso removerá permanentemente:\n• Todos os usuários\n• Todos os projetos/layouts\n• Assinatura e configurações Moodle\n\nEssa ação NÃO pode ser desfeita.`)) return;
        try {
            const r = await fetch(`${API}/master/tenants/${t.id}`, { method: 'DELETE', headers: authHeaders() });
            const d = await r.json();
            if (r.ok) { setMsg('✅ Cliente excluído.'); load(); }
            else { setMsg('❌ ' + d.error); }
        } catch { setMsg('❌ Erro de rede.'); }
    };

    const handleSyncAsaas = async (t) => {
        try {
            const r = await fetch(`${API}/master/tenants/${t.id}/sync-asaas-payment`, { method: 'POST', headers: authHeaders() });
            const d = await r.json();
            if (d.ok) {
                setMsg(`✅ Pagamento Asaas sincronizado para ${t.name}${d.planId ? ` (plano ${d.planId})` : ''}.`);
                load();
            } else {
                setMsg(`❌ Sync Asaas: ${d.reason || d.error || 'Não foi possível sincronizar.'}`);
            }
        } catch {
            setMsg('❌ Erro de rede ao sincronizar Asaas.');
        }
    };

    const filtered = tenants.filter((t) => {
        const q = search.trim().toLowerCase();
        if (q && !t.name.toLowerCase().includes(q) && !String(t.slug || '').toLowerCase().includes(q)) return false;
        if (filterPlan && t.current_plan_id !== filterPlan) return false;
        if (filterStatus && t.status !== filterStatus) return false;
        if (dateFrom || dateTo) {
            const signupDay = tenantSignupDateInput(t);
            if (!signupDay) return false;
            if (dateFrom && signupDay < dateFrom) return false;
            if (dateTo && signupDay > dateTo) return false;
        }
        return true;
    });
    const perPage = 15;
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, filtered.length);
    const paged = filtered.slice(startIdx, endIdx);
    const fmtSignupDate = (tenant) => {
        const d = tenantSignupDate(tenant);
        return d ? d.toLocaleDateString('pt-BR') : '—';
    };

    useEffect(() => {
        // Mudou filtro ou busca -> volta para a primeira página.
        setPage(1);
    }, [search, filterPlan, filterStatus, dateFrom, dateTo]);
    useEffect(() => {
        // Evita ficar em páginas inválidas após atualizar lista.
        if (page !== safePage) setPage(safePage);
    }, [filtered.length, totalPages]);

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="m-0 text-slate-800 dark:text-slate-200">🏢 Clientes</h2>
                <button type="button" onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-semibold">
                    {showForm ? '✕ Fechar' : '+ Novo Cliente'}
                </button>
            </div>

            {msg && <div className={`p-3 rounded-lg font-semibold mb-4 ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div>}

            {showForm && (
                <form onSubmit={handleCreate} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm mb-6 border border-slate-200 dark:border-slate-700">
                    <h3 className="m-0 mb-4 text-slate-800 dark:text-slate-200">Novo Cliente</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        {[['Nome da Empresa', 'name', 'Escola de Exemplo'], ['Slug (subdomínio)', 'slug', 'escola-exemplo'],
                        ['Nome do Owner', 'ownerName', 'João Silva'], ['Email do Owner', 'ownerEmail', 'joao@escola.com'],
                        ['Senha Inicial', 'ownerPassword', 'Senha@2025!']].map(([label, key, ph]) => (
                            <div key={key}>
                                <label className={labelCls}>{label}</label>
                                <input required={['name', 'slug', 'ownerEmail'].includes(key)} type={key === 'ownerPassword' ? 'password' : 'text'} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} placeholder={ph} className={inputCls} />
                            </div>
                        ))}
                        <div>
                            <label className={labelCls}>Plano Inicial</label>
                            <select value={form.planId} onChange={e => setForm(p => ({ ...p, planId: e.target.value }))} className={inputCls}>
                                {plans.map(pl => <option key={pl.id} value={pl.id}>{pl.name} — R$ {parseFloat(pl.price_brl).toFixed(2)}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg mb-4">
                        ⚠️ O slug define o subdomínio: <strong>{form.slug || 'slug'}.geppetto-creator.com</strong>
                    </div>
                    <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-bold disabled:opacity-60">
                        {saving ? 'Criando...' : 'Criar Cliente'}
                    </button>
                </form>
            )}

            <div className="flex flex-wrap gap-3 mb-4 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className={labelCls}>Buscar</label>
                    <input
                        type="search"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Nome ou slug…"
                        className={inputCls}
                    />
                </div>
                <div className="min-w-[160px]">
                    <label className={labelCls}>Plano</label>
                    <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className={inputCls}>
                        <option value="">Todos</option>
                        {plans.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
                    </select>
                </div>
                <div className="min-w-[140px]">
                    <label className={labelCls}>Status</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inputCls}>
                        <option value="">Todos</option>
                        <option value="active">Ativo</option>
                        <option value="trial">Trial</option>
                        <option value="pending">Pendente</option>
                        <option value="overdue">Em atraso</option>
                        <option value="suspended">Suspenso</option>
                        <option value="cancelled">Cancelado</option>
                    </select>
                </div>
                <div className="min-w-[140px]">
                    <label className={labelCls}>Inscrição de</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} />
                </div>
                <div className="min-w-[140px]">
                    <label className={labelCls}>Inscrição até</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls} />
                </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700">
                {loading ? <div className="p-8 text-center text-slate-400 dark:text-slate-500">⏳ Carregando...</div>
                    : filtered.length === 0 ? <div className="p-8 text-center text-slate-400 dark:text-slate-500">Nenhum cliente encontrado.</div>
                        : paged.map((t, i) => (
                            <div key={t.id} className={`p-4 px-5 flex items-center gap-4 flex-wrap ${i < paged.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''}`}>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="font-bold text-slate-800 dark:text-slate-200">{t.name}</div>
                                    <div className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-md" title={fmtTenantApiUrl(t)}>
                                        {fmtTenantApiUrl(t)}
                                    </div>
                                    {t.owner_email && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">👤 {t.owner_name || '—'} · {t.owner_email}</div>}
                                </div>
                                <Badge status={t.status} />
                                <div className="text-sm text-slate-600 dark:text-slate-400">
                                    <span className="font-semibold">{t.plan_name || '—'}</span>
                                    {t.plan_price > 0 && ` · R$ ${parseFloat(t.plan_price).toFixed(2)}/mês`}
                                </div>
                                <div className="text-xs text-slate-400 dark:text-slate-500">
                                    👤 {t.user_count || 0} · 📁 {t.project_count || 0}
                                </div>
                                <div className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap" title="Data de inscrição">
                                    📅 {fmtSignupDate(t)}
                                </div>
                                <div className="flex gap-1.5 flex-wrap">
                                    <button type="button" onClick={() => setEditingTenant(t)} className="px-3 py-1 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 cursor-pointer text-xs font-semibold">
                                        ✏️ Editar
                                    </button>
                                    <button type="button" onClick={() => handleAccess(t)} className="px-3 py-1 rounded-md bg-blue-600 text-white border-none cursor-pointer text-xs font-semibold">
                                        ⚡ Acessar
                                    </button>
                                    <button type="button" onClick={() => toggleStatus(t)} className={`px-3 py-1 rounded-md border-none cursor-pointer text-xs font-semibold ${t.status === 'active' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200' : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'}`}>
                                        {t.status === 'active' ? '⏸ Suspender' : '▶ Ativar'}
                                    </button>
                                    <button type="button" onClick={() => handleDelete(t)} className="px-3 py-1 rounded-md bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-none cursor-pointer text-xs font-semibold">
                                        🗑️ Excluir
                                    </button>
                                    <button type="button" onClick={() => handleSyncAsaas(t)} className="px-3 py-1 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-none cursor-pointer text-xs font-semibold" title="Sincronizar pagamento/assinatura Asaas (quando webhook falhou)">
                                        💳 Sync Asaas
                                    </button>
                                    <button type="button" onClick={async () => { try { await fetch(`${API}/master/leadlovers/sync/${t.id}`, { method: 'POST', headers: authHeaders() }); } catch {} }} className="px-3 py-1 rounded-md bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200 border-none cursor-pointer text-xs font-semibold" title="Sincronizar com Leadlovers">
                                        📧 Sync
                                    </button>
                                </div>
                            </div>
                        ))}
            </div>

            {!loading && filtered.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                        Mostrando <strong className="font-semibold">{filtered.length === 0 ? 0 : (startIdx + 1)}</strong>–<strong className="font-semibold">{endIdx}</strong> de <strong className="font-semibold">{filtered.length}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={safePage <= 1}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50 cursor-pointer"
                        >
                            ← Anterior
                        </button>
                        <div className="text-xs text-slate-600 dark:text-slate-300 px-2">
                            Página <strong className="font-semibold">{safePage}</strong> de <strong className="font-semibold">{totalPages}</strong>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={safePage >= totalPages}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50 cursor-pointer"
                        >
                            Próxima →
                        </button>
                    </div>
                </div>
            )}

            {editingTenant && (
                <EditTenantModal
                    tenant={editingTenant}
                    plans={plans}
                    onClose={() => setEditingTenant(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
};

// ─── PLANOS ───────────────────────────────────────────────────
const CURRENCY_TABS_MODAL = [
    { id: 'brl', label: 'Real (R$)', labelKey: 'master:currencyBRL' },
    { id: 'usd', label: 'Dólar (US$)', labelKey: 'master:currencyUSD' },
    { id: 'eur', label: 'Euro (€)', labelKey: 'master:currencyEUR' },
];

const PlanConfigModal = ({ plan, onClose, onSave }) => {
    const { t } = useTranslation(['master']);
    const [currencyTab, setCurrencyTab] = useState('brl');
    const normalizePlanFeaturesForEdit = (raw) => {
        const f = raw && typeof raw === 'object' ? { ...raw } : { grids: [], elements: [], modules: {} };
        if (f.save === true) {
            f.save = 9999;
        } else {
            const n = parseInt(f.save, 10);
            f.save = Number.isFinite(n) && n >= 0 ? n : 0;
        }
        return f;
    };
    const [features, setFeatures] = useState(() => normalizePlanFeaturesForEdit(plan.features));
    const [priceBrlMonthly, setPriceBrlMonthly] = useState(plan.price_brl_monthly ?? plan.price_brl ?? 0);
    const [priceBrlAnnual, setPriceBrlAnnual] = useState(plan.price_brl_annual ?? (plan.price_brl ? plan.price_brl * 12 * 0.67 : 0));
    const [priceUsdMonthly, setPriceUsdMonthly] = useState(plan.price_usd ?? 0);
    const [priceUsdAnnual, setPriceUsdAnnual] = useState(plan.price_usd_annual ?? 0);
    const [priceEurMonthly, setPriceEurMonthly] = useState(plan.price_eur ?? 0);
    const [priceEurAnnual, setPriceEurAnnual] = useState(plan.price_eur_annual ?? 0);
    const [trialDays, setTrialDays] = useState(plan.trial_days ?? '');
    const [showOnProfile, setShowOnProfile] = useState(() => plan.show_on_profile !== 0 && plan.show_on_profile !== '0');
    const [isAdminPlan, setIsAdminPlan] = useState(() => plan.is_admin_plan === 1 || plan.is_admin_plan === '1' || plan.is_admin_plan === true);
    const [storageProjMb, setStorageProjMb] = useState(plan.storage_projects_mb != null ? String(plan.storage_projects_mb) : '');
    const [storageLogsMb, setStorageLogsMb] = useState(plan.storage_logs_mb != null ? String(plan.storage_logs_mb) : '');
    const [storageMediaMb, setStorageMediaMb] = useState(plan.storage_media_mb != null ? String(plan.storage_media_mb) : '');
    const [saving, setSaving] = useState(false);
    const [mergedTierMap, setMergedTierMap] = useState(null);
    const [elementCategories, setElementCategories] = useState([]);
    const [elementExtraIds, setElementExtraIds] = useState([]);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/feature-flags`)
            .then((r) => r.json())
            .then((d) => {
                if (cancelled) return;
                const map = getMergedElementTiers(d.flags || {});
                setMergedTierMap(map);
                const derived = deriveElementCategoryStateFromPlan(plan.features, map);
                setElementCategories(derived.element_categories);
                setElementExtraIds(derived.element_extra_ids);
            })
            .catch(() => {
                if (cancelled) return;
                const map = getMergedElementTiers({});
                setMergedTierMap(map);
                const derived = deriveElementCategoryStateFromPlan(plan.features, map);
                setElementCategories(derived.element_categories);
                setElementExtraIds(derived.element_extra_ids);
            });
        return () => { cancelled = true; };
    }, [plan.id]);

    const allGrids = ALL_GRIDS.map((g) => ({
        id: g.id,
        label: g.id === 'dynamic' ? 'Dinâmico' : g.label.replace(/^Grid\s+/i, ''),
    }));

    const allModules = ALL_MODULES.map((m) => ({
        id: m.id,
        label: m.id === 'plugin' ? 'Botão Plugin Moodle' : m.label,
    }));

    const toggle = (list, id) => {
        const current = features[list] || [];
        const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
        setFeatures({ ...features, [list]: next });
    };

    const toggleModule = (id) => {
        const mods = features.modules || {};
        setFeatures({ ...features, modules: { ...mods, [id]: !mods[id] } });
    };

    const selectAll = (list, items) => {
        setFeatures({ ...features, [list]: items.map(i => i.id) });
    };

    const toggleElementExtra = (id) => {
        setElementExtraIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /** Teto do plano (ceiling): basic ⊂ expert ⊂ premium. */
    const planMaxElementTier = resolveMaxElementTier({ element_categories: elementCategories }) || null;

    const setPlanMaxElementTier = (tier) => {
        if (!tier) {
            setElementCategories([]);
            return;
        }
        setElementCategories(categoriesUpToMaxTier(tier));
        setElementExtraIds((prev) =>
            prev.filter((id) => {
                const t = (mergedTierMap && mergedTierMap[id]) || 'premium';
                return ELEMENT_TIER_ORDER.indexOf(t) > ELEMENT_TIER_ORDER.indexOf(tier);
            })
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const parseMbSave = (s) => {
                if (s === '' || s == null) return null;
                const x = parseInt(String(s), 10);
                return Number.isFinite(x) ? x : null;
            };
            const tierMap = mergedTierMap || getMergedElementTiers({});
            const maxTier = resolveMaxElementTier({ element_categories: elementCategories });
            const normalizedCategories = maxTier ? categoriesUpToMaxTier(maxTier) : [];
            const resolvedElements = [...resolvePlanAllowedElements({
                element_categories: normalizedCategories,
                max_element_tier: maxTier || undefined,
                element_extra_ids: elementExtraIds,
                elements: [],
            }, tierMap)];
            const ok = await onSave(plan.id, {
                features: {
                    ...features,
                    element_categories: normalizedCategories,
                    max_element_tier: maxTier || null,
                    element_extra_ids: elementExtraIds,
                    elements: resolvedElements,
                },
                show_on_profile: showOnProfile ? 1 : 0,
                is_admin_plan: isAdminPlan ? 1 : 0,
                price_brl_monthly: parseFloat(priceBrlMonthly) || 0,
                price_brl_annual: parseFloat(priceBrlAnnual) || 0,
                price_usd: parseFloat(priceUsdMonthly) || 0,
                price_usd_annual: parseFloat(priceUsdAnnual) || 0,
                price_eur: parseFloat(priceEurMonthly) || 0,
                price_eur_annual: parseFloat(priceEurAnnual) || 0,
                trial_days: trialDays === '' ? null : parseInt(trialDays, 10) || 0,
                storage_projects_mb: parseMbSave(storageProjMb),
                storage_logs_mb: parseMbSave(storageLogsMb),
                storage_media_mb: parseMbSave(storageMediaMb),
            });
            if (ok) onClose();
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';
    const hintCls = 'text-xs text-slate-500 dark:text-slate-400 mt-1';

    return (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
                <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h3 className="m-0 text-lg font-semibold text-slate-800 dark:text-slate-200">⚙️ {t('master:configurePlan')}: <b>{plan.name}</b></h3>
                    <button onClick={onClose} className="bg-transparent border-none text-2xl cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">&times;</button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <div className="mb-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-600">
                        <h4 className="mb-2 text-sm font-bold text-slate-800 dark:text-slate-200">{t('master:planShowOnProfileSection')}</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">{t('master:planShowOnProfileHint')}</p>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showOnProfile}
                                onChange={(e) => setShowOnProfile(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">{t('master:planShowOnProfileLabel')}</span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer mt-3">
                            <input
                                type="checkbox"
                                checked={isAdminPlan}
                                onChange={(e) => setIsAdminPlan(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">{t('master:planAdminPlanLabel')}</span>
                        </label>
                        {isAdminPlan && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">{t('master:planAdminPlanHint')}</p>
                        )}
                    </div>

                    {/* PERÍODO TRIAL - destaque no topo */}
                    <div className="mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700">
                        <h4 className="mb-2 text-sm font-bold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                            <span>⏳</span> {t('master:trialSectionTitle')}
                        </h4>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">{t('master:trialDaysHint')}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                            <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="0"
                                value={trialDays}
                                onChange={e => setTrialDays(e.target.value)}
                                className="w-24 px-3 py-2 border-2 border-amber-300 dark:border-amber-600 rounded-lg text-sm font-semibold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800"
                            />
                            <span className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                                {trialDays ? t('master:trialDaysLabel', { days: trialDays }) : t('master:noTrial')}
                            </span>
                        </div>
                    </div>

                    <div className="mb-6 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                        <h4 className="mb-2 text-sm font-bold text-violet-800 dark:text-violet-200">{t('master:storageQuotasTitle')}</h4>
                        <p className="text-xs text-violet-700 dark:text-violet-300 mb-3">{t('master:storageQuotasHint')}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className={labelCls}>{t('master:storageProjectsMb')}</label>
                                <input type="number" min="0" step="1" value={storageProjMb} onChange={(e) => setStorageProjMb(e.target.value)} className={inputCls} placeholder={t('master:storagePlaceholder')} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:storageLogsMb')}</label>
                                <input type="number" min="0" step="1" value={storageLogsMb} onChange={(e) => setStorageLogsMb(e.target.value)} className={inputCls} placeholder={t('master:storagePlaceholder')} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:storageMediaMb')}</label>
                                <input type="number" min="0" step="1" value={storageMediaMb} onChange={(e) => setStorageMediaMb(e.target.value)} className={inputCls} placeholder={t('master:storagePlaceholder')} />
                            </div>
                        </div>
                    </div>

                    {/* PREÇOS - Abas Real | Dólar | Euro */}
                    <div className="mb-8 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                        <h4 className="mb-4 uppercase text-xs font-bold text-emerald-700 dark:text-emerald-400">💰 {t('master:prices')}</h4>
                        {/* Abas de moeda */}
                        <div className="flex gap-1 p-1 mb-4 bg-slate-100 dark:bg-slate-700 rounded-xl w-fit">
                            {CURRENCY_TABS_MODAL.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setCurrencyTab(tab.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currencyTab === tab.id ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-slate-200 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
                                >
                                    {t(tab.labelKey)}
                                </button>
                            ))}
                        </div>
                        {/* Conteúdo por aba */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {currencyTab === 'brl' && (
                                <>
                                    <div>
                                        <label className={labelCls}>{t('master:monthlyPrice')}</label>
                                        <input type="number" step="0.01" min="0" value={priceBrlMonthly} onChange={e => setPriceBrlMonthly(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>{t('master:priceExampleMonthly')}</p>
                                    </div>
                                    <div>
                                        <label className={labelCls}>{t('master:annualPrice')}</label>
                                        <input type="number" step="0.01" min="0" value={priceBrlAnnual} onChange={e => setPriceBrlAnnual(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>{t('master:priceExampleAnnual')}</p>
                                    </div>
                                </>
                            )}
                            {currencyTab === 'usd' && (
                                <>
                                    <div>
                                        <label className={labelCls}>USD/mês (Stripe)</label>
                                        <input type="number" step="0.01" min="0" value={priceUsdMonthly} onChange={e => setPriceUsdMonthly(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>Ex: 9.99</p>
                                    </div>
                                    <div>
                                        <label className={labelCls}>USD/ano (Stripe)</label>
                                        <input type="number" step="0.01" min="0" value={priceUsdAnnual} onChange={e => setPriceUsdAnnual(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>Ex: 99.90 (total/ano)</p>
                                    </div>
                                </>
                            )}
                            {currencyTab === 'eur' && (
                                <>
                                    <div>
                                        <label className={labelCls}>EUR/mês (Stripe)</label>
                                        <input type="number" step="0.01" min="0" value={priceEurMonthly} onChange={e => setPriceEurMonthly(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>Ex: 8.99</p>
                                    </div>
                                    <div>
                                        <label className={labelCls}>EUR/ano (Stripe)</label>
                                        <input type="number" step="0.01" min="0" value={priceEurAnnual} onChange={e => setPriceEurAnnual(e.target.value)} className={inputCls} />
                                        <p className={hintCls}>Ex: 89.90 (total/ano)</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* FUNCIONALIDADES PRINCIPAIS */}
                    <div className="mb-8 p-4 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                        <h4 className="mb-2 uppercase text-xs font-bold text-sky-700 dark:text-sky-400">💾 {t('master:mainFeatures')}</h4>
                        <p className="mb-4 text-xs text-sky-800 dark:text-sky-200/90 leading-relaxed m-0">{t('master:saveLayoutSectionLead')}</p>
                        <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
                            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">{t('master:saveLayout')}</label>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={features.save ?? 0}
                                onChange={(e) => {
                                    const n = Math.max(0, parseInt(e.target.value, 10) || 0);
                                    setFeatures({ ...features, save: n });
                                }}
                                className={inputCls}
                            />
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('master:saveLayoutHint')}</p>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>{t('master:collabEditorsMax')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={features.max_collaborators_editor ?? 0}
                                    onChange={(e) => {
                                        const n = Math.max(0, parseInt(e.target.value, 10) || 0);
                                        setFeatures({ ...features, max_collaborators_editor: n });
                                    }}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{t('master:collabViewersMax')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={features.max_collaborators_viewer ?? 0}
                                    onChange={(e) => {
                                        const n = Math.max(0, parseInt(e.target.value, 10) || 0);
                                        setFeatures({ ...features, max_collaborators_viewer: n });
                                    }}
                                    className={inputCls}
                                />
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('master:collabLimitsHint')}</p>
                    </div>

                    {/* GRIDS */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="m-0 uppercase text-xs font-bold text-slate-600 dark:text-slate-400">📐 Modelos (Grids)</h4>
                            <button type="button" onClick={() => selectAll('grids', allGrids)} className="text-xs text-blue-600 dark:text-blue-400 border-none bg-transparent cursor-pointer hover:underline">Selecionar Todos</button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {allGrids.map(g => (
                                <label key={g.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">
                                    <input type="checkbox" checked={features.grids?.includes(g.id)} onChange={() => toggle('grids', g.id)} />
                                    {g.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* ELEMENTOS — teto por categoria (ceiling) + avulsos */}
                    <div className="mb-8">
                        <div className="flex flex-col gap-2 mb-4">
                            <h4 className="m-0 uppercase text-xs font-bold text-slate-600 dark:text-slate-400">🧩 Elementos do plano</h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 m-0 leading-relaxed">
                                Defina o <strong>teto de categoria</strong> (Básico → Expert → Premium). O plano libera <strong>tudo até esse teto</strong>.
                                Avulsos permitem elementos acima do teto. Mover um elemento na <em>Classificação de Recursos</em> atualiza o acesso automaticamente.
                                O canvas continua livre; o teto vale no <strong>export / save na nuvem</strong> e nos recursos dinâmicos do Moodle.
                            </p>
                        </div>
                        {!mergedTierMap ? (
                            <p className="text-sm text-slate-500 dark:text-slate-400">Carregando classificação de elementos…</p>
                        ) : (
                            <div className="space-y-5">
                                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-600">
                                    <h5 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-3">Teto incluído no plano</h5>
                                    <div className="flex flex-col gap-2.5 mb-3">
                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                                            <input
                                                type="radio"
                                                name={`plan-max-tier-${plan.id}`}
                                                checked={planMaxElementTier === 'basic'}
                                                onChange={() => setPlanMaxElementTier('basic')}
                                            />
                                            Até Básico <span className="text-slate-400 font-normal">(sem ícone na gaveta)</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                                            <input
                                                type="radio"
                                                name={`plan-max-tier-${plan.id}`}
                                                checked={planMaxElementTier === 'expert'}
                                                onChange={() => setPlanMaxElementTier('expert')}
                                            />
                                            Até Expert <span className="text-violet-500" aria-hidden>🪄</span>
                                            <span className="text-slate-400 font-normal">(inclui Básico)</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                                            <input
                                                type="radio"
                                                name={`plan-max-tier-${plan.id}`}
                                                checked={planMaxElementTier === 'premium'}
                                                onChange={() => setPlanMaxElementTier('premium')}
                                            />
                                            Até Premium <span className="text-amber-500" aria-hidden>👑</span>
                                            <span className="text-slate-400 font-normal">(inclui Básico e Expert)</span>
                                        </label>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button type="button" onClick={() => setPlanMaxElementTier('premium')} className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white border-none cursor-pointer font-semibold">
                                            Teto Premium (tudo)
                                        </button>
                                        <button type="button" onClick={() => setPlanMaxElementTier(null)} className="text-xs px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 cursor-pointer font-semibold text-slate-700 dark:text-slate-200">
                                            Limpar teto (legado)
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <h5 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                                        Avulsos <span className="font-normal normal-case text-slate-400">(acima do teto)</span>
                                    </h5>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                        {ALL_ELEMENTS.filter((e) => {
                                            if (!planMaxElementTier) return true;
                                            const t = mergedTierMap[e.id] || 'basic';
                                            return ELEMENT_TIER_ORDER.indexOf(t) > ELEMENT_TIER_ORDER.indexOf(planMaxElementTier);
                                        }).map((e) => (
                                            <label key={e.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">
                                                <input type="checkbox" checked={elementExtraIds.includes(e.id)} onChange={() => toggleElementExtra(e.id)} />
                                                <span className="flex items-center gap-1.5">
                                                    {e.label}
                                                    {mergedTierMap[e.id] === 'expert' && <span className="text-violet-500 text-[10px]" aria-hidden>🪄</span>}
                                                    {mergedTierMap[e.id] === 'premium' && <span className="text-amber-500 text-[10px]" aria-hidden>👑</span>}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* MÓDULOS GLOBAIS */}
                    <div>
                        <h4 className="mb-4 uppercase text-xs font-bold text-slate-600 dark:text-slate-400">⚙️ Módulos e Abas</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {allModules.map(m => (
                                <label key={m.id} className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 cursor-pointer text-sm text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-600">
                                    <input type="checkbox" checked={features.modules?.[m.id]} onChange={() => toggleModule(m.id)} />
                                    {m.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 cursor-pointer font-semibold text-slate-800 dark:text-slate-200">{t('master:cancel')}</button>
                    <button type="button" onClick={handleSave} disabled={saving} className="px-6 py-2 border-none rounded-lg bg-blue-600 text-white cursor-pointer font-bold disabled:opacity-60">
                        {saving ? 'Gravando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FeatureFlagsManager = () => {
    const [flags, setFlags] = useState({
        expert: [], premium: [], trial: [], premiumGrids: [], trialGrids: [], premiumModules: [], trialModules: [],
        elementTiers: {}, gridTiers: {}, moduleTiers: {},
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetch(`${API}/feature-flags`).then((r) => r.json()).then((d) => {
            if (d.flags) {
                const f = d.flags;
                const elementTiers = getMergedElementTiers(f);
                const gridTiers = getMergedGridTiers(f);
                const moduleTiers = getMergedModuleTiers(f);
                const expert = ALL_ELEMENTS.filter((e) => elementTiers[e.id] === 'expert').map((e) => e.id);
                const premium = ALL_ELEMENTS.filter((e) => elementTiers[e.id] === 'premium').map((e) => e.id);
                const premiumGrids = ALL_GRIDS.filter((g) => gridTiers[g.id] === 'premium').map((g) => g.id);
                const premiumModules = ALL_MODULES.filter((m) => moduleTiers[m.id] === 'premium').map((m) => m.id);
                setFlags({
                    expert,
                    premium,
                    trial: f.trial || [],
                    premiumGrids,
                    trialGrids: [],
                    premiumModules,
                    trialModules: [],
                    elementTiers,
                    gridTiers,
                    moduleTiers,
                });
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const toggle = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

    const setElementTierForId = (id, tier) => {
        setFlags((f) => {
            const elementTiers = { ...getMergedElementTiers(f), [id]: tier };
            return {
                ...f,
                elementTiers,
                expert: ALL_ELEMENTS.filter((e) => elementTiers[e.id] === 'expert').map((e) => e.id),
                premium: ALL_ELEMENTS.filter((e) => elementTiers[e.id] === 'premium').map((e) => e.id),
            };
        });
    };

    const setGridTierForId = (id, tier) => {
        setFlags((f) => {
            const gridTiers = { ...getMergedGridTiers(f), [id]: tier };
            return {
                ...f,
                gridTiers,
                premiumGrids: ALL_GRIDS.filter((g) => gridTiers[g.id] === 'premium').map((g) => g.id),
                trialGrids: [],
            };
        });
    };

    const setModuleTierForId = (id, tier) => {
        setFlags((f) => {
            const moduleTiers = { ...getMergedModuleTiers(f), [id]: tier };
            return {
                ...f,
                moduleTiers,
                premiumModules: ALL_MODULES.filter((m) => moduleTiers[m.id] === 'premium').map((m) => m.id),
                trialModules: [],
            };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        const fullTiers = getMergedElementTiers({ ...flags, elementTiers: flags.elementTiers || {} });
        const fullGridTiers = getMergedGridTiers({ ...flags, gridTiers: flags.gridTiers || {} });
        const fullModuleTiers = getMergedModuleTiers({ ...flags, moduleTiers: flags.moduleTiers || {} });
        const payload = {
            ...flags,
            expert: ALL_ELEMENTS.filter((e) => fullTiers[e.id] === 'expert').map((e) => e.id),
            premium: ALL_ELEMENTS.filter((e) => fullTiers[e.id] === 'premium').map((e) => e.id),
            elementTiers: fullTiers,
            gridTiers: fullGridTiers,
            moduleTiers: fullModuleTiers,
            premiumGrids: ALL_GRIDS.filter((g) => fullGridTiers[g.id] === 'premium').map((g) => g.id),
            premiumModules: ALL_MODULES.filter((m) => fullModuleTiers[m.id] === 'premium').map((m) => m.id),
            trialGrids: [],
            trialModules: [],
        };
        await fetch(`${API}/master/feature-flags`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ flags: payload }) });
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    if (loading) return <div className="p-8 text-center text-slate-400 dark:text-slate-500">Carregando flags...</div>;

    const chipCls = (active) => `inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all border-2 ${active ? '' : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500'}`;

    const tierFor = (id) => (flags.elementTiers && flags.elementTiers[id]) || getMergedElementTiers(flags)[id] || 'basic';
    const gridTierFor = (id) => getMergedGridTiers(flags)[id] || 'basic';
    const moduleTierFor = (id) => getMergedModuleTiers(flags)[id] || 'basic';

    const renderGridTierFlags = () => (
        <div className="mb-6">
            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Grids / Modelos</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 m-0">
                Categoria por modelo (inclui <strong>Grid dinâmico</strong>). No plano grátis, Expert e Premium ficam bloqueados para inserção.
            </p>
            <div className="flex flex-wrap gap-2">
                {ALL_GRIDS.map((item) => {
                    const cur = gridTierFor(item.id);
                    const tierBtn = (tier, label, border) => (
                        <button
                            type="button"
                            key={tier}
                            onClick={() => setGridTierForId(item.id, tier)}
                            className={chipCls(cur === tier)}
                            style={cur === tier ? { borderColor: border, background: `${border}15`, color: border } : {}}
                            title={label}
                        >
                            {label}
                        </button>
                    );
                    return (
                        <div key={item.id} className="flex gap-1 items-center flex-wrap border border-slate-100 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-slate-50/50 dark:bg-slate-900/30">
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 min-w-[120px]">{item.id === 'dynamic' ? 'Dinâmico' : item.label.replace(/^Grid\s+/i, '')}</span>
                            {tierBtn('basic', 'Básico', '#64748b')}
                            {tierBtn('expert', '🪄 Expert', '#7c3aed')}
                            {tierBtn('premium', '👑 Premium', '#f59e0b')}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderModuleTierFlags = () => (
        <div className="mb-6">
            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Módulos e abas (menu lateral)</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 m-0">
                Ícones no menu do builder; no plano grátis, Expert e Premium permanecem como hoje (conforme regras de módulos do plano).
            </p>
            <div className="flex flex-wrap gap-2">
                {ALL_MODULES.map((item) => {
                    const cur = moduleTierFor(item.id);
                    const tierBtn = (tier, label, border) => (
                        <button
                            type="button"
                            key={tier}
                            onClick={() => setModuleTierForId(item.id, tier)}
                            className={chipCls(cur === tier)}
                            style={cur === tier ? { borderColor: border, background: `${border}15`, color: border } : {}}
                            title={label}
                        >
                            {label}
                        </button>
                    );
                    return (
                        <div key={item.id} className="flex gap-1 items-center flex-wrap border border-slate-100 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-slate-50/50 dark:bg-slate-900/30">
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 min-w-[100px]">{item.label}</span>
                            {tierBtn('basic', 'Básico', '#64748b')}
                            {tierBtn('expert', '🪄 Expert', '#7c3aed')}
                            {tierBtn('premium', '👑 Premium', '#f59e0b')}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderElementFlags = () => (
        <div className="mb-6">
            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Elementos — categoria (ícone na gaveta)</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 m-0">
                Escolha <strong>uma</strong> categoria por elemento: <strong>Básico</strong> (sem ícone), <strong>Expert 🪄</strong> ou <strong>Premium 👑</strong>. No plano grátis, Expert e Premium ficam bloqueados (exceto trial 🧪 e exceções do sistema).
            </p>
            <div className="flex flex-wrap gap-2">
                {ALL_ELEMENTS.map((item) => {
                    const cur = tierFor(item.id);
                    const isTrial = flags.trial.includes(item.id);
                    const tierBtn = (tier, label, border) => (
                        <button
                            type="button"
                            key={tier}
                            onClick={() => setElementTierForId(item.id, tier)}
                            className={chipCls(cur === tier)}
                            style={cur === tier ? { borderColor: border, background: `${border}15`, color: border } : {}}
                            title={label}
                        >
                            {label}
                        </button>
                    );
                    return (
                        <div key={item.id} className="flex gap-1 items-center flex-wrap border border-slate-100 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-slate-50/50 dark:bg-slate-900/30">
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 min-w-[100px]">{item.label}</span>
                            {tierBtn('basic', 'Básico', '#64748b')}
                            {tierBtn('expert', '🪄 Expert', '#7c3aed')}
                            {tierBtn('premium', '👑 Premium', '#f59e0b')}
                            <button type="button" onClick={() => setFlags((f) => ({ ...f, trial: toggle(f.trial, item.id) }))} className={chipCls(isTrial)} style={isTrial ? { borderColor: '#3b82f6', background: '#3b82f615', color: '#3b82f6' } : {}} title="Trial no grátis">
                                🧪 {isTrial ? 'Trial' : '—'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm mt-8 border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="m-0 text-slate-800 dark:text-slate-200">🎨 Classificação de Recursos</h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Define <strong>Básico</strong> (sem ícone), <strong>Expert 🪄</strong> e <strong>Premium 👑</strong> para elementos, grids e módulos do menu. <strong>Trial 🧪</strong> continua só nos elementos. Em <strong>Planos</strong>, o teto de categoria libera tudo até aquele nível (ceiling).
                    </p>
                </div>
                <button type="button" onClick={handleSave} disabled={saving} className={`px-6 py-2 border-none rounded-lg cursor-pointer font-bold text-sm ${saved ? 'bg-emerald-600' : 'bg-blue-600'} text-white disabled:opacity-60`}>
                    {saving ? '...' : saved ? '✅ Salvo!' : '💾 Salvar'}
                </button>
            </div>

            {renderElementFlags()}
            {renderGridTierFlags()}
            {renderModuleTierFlags()}
        </div>
    );
};

const ADDON_GRANT_KEYS = ['editor', 'viewer', 'save_slot', 'storage_projects', 'storage_logs', 'storage_media'];

function addonGrantMasterLabel(t, gt) {
    const k = `master:addonGrant_${gt}`;
    return t ? t(k) : gt;
}

const PlanAddonsMasterView = ({ t }) => {
    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [createForm, setCreateForm] = useState({
        grant_type: 'editor',
        grant_quantity: 1,
        name_pt: '',
        name_en: '',
        description_pt: '',
        description_en: '',
        price_brl_monthly: 0,
        price_brl_semiannual: 0,
        price_brl_annual: 0,
        sort_order: 0,
    });

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${API}/master/plan-addons`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => setAddons(d.addons || []))
            .catch(() => setAddons([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';

    const saveEdit = async () => {
        if (!editing) return;
        setSaving(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/plan-addons/${editing.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    grant_type: editing.grant_type,
                    grant_quantity: editing.grant_quantity,
                    name_pt: editing.name_pt,
                    name_en: editing.name_en,
                    description_pt: editing.description_pt,
                    description_en: editing.description_en,
                    price_brl_monthly: editing.price_brl_monthly,
                    price_brl_semiannual: editing.price_brl_semiannual,
                    price_brl_annual: editing.price_brl_annual,
                    is_active: editing.is_active ? 1 : 0,
                    sort_order: editing.sort_order,
                }),
            });
            const d = await r.json();
            if (!r.ok) setMsg('❌ ' + (d.error || 'Erro'));
            else {
                setMsg('✅ Salvo.');
                setEditing(null);
                load();
            }
        } catch (_) {
            setMsg('❌ Erro de rede.');
        }
        setSaving(false);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/plan-addons`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(createForm),
            });
            const d = await r.json();
            if (!r.ok) setMsg('❌ ' + (d.error || 'Erro'));
            else {
                setMsg('✅ Add-on criado.');
                setCreateForm({
                    grant_type: 'editor',
                    grant_quantity: 1,
                    name_pt: '',
                    name_en: '',
                    description_pt: '',
                    description_en: '',
                    price_brl_monthly: 0,
                    price_brl_semiannual: 0,
                    price_brl_annual: 0,
                    sort_order: 0,
                });
                load();
            }
        } catch (_) {
            setMsg('❌ Erro de rede.');
        }
        setSaving(false);
    };

    const deactivate = async (id) => {
        if (!window.confirm('Desativar este add-on na vitrine?')) return;
        await fetch(`${API}/master/plan-addons/${id}`, { method: 'DELETE', headers: authHeaders() });
        load();
    };

    return (
        <div>
            <h2 className="m-0 mb-2 text-slate-800 dark:text-slate-200">{t ? t('master:addonsMasterTitle') : 'Add-ons'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-3xl">{t ? t('master:addonsMasterIntro') : ''}</p>
            {msg ? <div className={`p-3 rounded-lg font-semibold mb-4 ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div> : null}
            {loading ? (
                <p className="text-slate-500 dark:text-slate-400">{t ? t('master:loading') : '…'}</p>
            ) : (
                <div className="space-y-3 mb-10">
                    {addons.map((a) => (
                        <div key={a.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700 flex flex-wrap gap-4 items-center justify-between">
                            <div className="min-w-[200px]">
                                <div className="font-bold text-slate-800 dark:text-slate-200">{a.name_pt}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{a.name_en}</div>
                                <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">{addonGrantMasterLabel(t, a.grant_type)} × {a.grant_quantity}</div>
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-300 font-mono">
                                {parseFloat(a.price_brl_monthly || 0).toFixed(2)} / {parseFloat(a.price_brl_semiannual || 0).toFixed(2)} / {parseFloat(a.price_brl_annual || 0).toFixed(2)}
                            </div>
                            <div className="flex gap-2 items-center">
                                <span className="text-sm" title={a.is_active ? 'Ativo' : 'Inativo'}>{a.is_active ? '✅' : '🔴'}</span>
                                <button type="button" onClick={() => setEditing({ ...a, is_active: a.is_active ? 1 : 0 })} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold border-none cursor-pointer">
                                    {t ? t('master:addonsEdit') : 'Editar'}
                                </button>
                                <button type="button" onClick={() => deactivate(a.id)} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 rounded-lg text-sm font-bold border-none cursor-pointer">
                                    {t ? t('master:addonsDeactivate') : 'Desativar'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <h3 className="text-slate-800 dark:text-slate-200 mb-4">{t ? t('master:addonsCreateTitle') : 'Novo add-on'}</h3>
            <form onSubmit={handleCreate} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className={labelCls}>{t ? t('master:addonsTableGrant') : 'Tipo'}</label>
                    <select className={inputCls} value={createForm.grant_type} onChange={(e) => setCreateForm((f) => ({ ...f, grant_type: e.target.value }))}>
                        {ADDON_GRANT_KEYS.map((k) => (
                            <option key={k} value={k}>{addonGrantMasterLabel(t, k)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:addonsTableQty') : 'Qtd'}</label>
                    <input type="number" min={1} className={inputCls} value={createForm.grant_quantity} onChange={(e) => setCreateForm((f) => ({ ...f, grant_quantity: parseInt(e.target.value, 10) || 1 }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:addonsTableOrder') : 'Ordem'}</label>
                    <input type="number" className={inputCls} value={createForm.sort_order} onChange={(e) => setCreateForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} />
                </div>
                <div>
                    <label className={labelCls}>Nome PT</label>
                    <input required className={inputCls} value={createForm.name_pt} onChange={(e) => setCreateForm((f) => ({ ...f, name_pt: e.target.value }))} />
                </div>
                <div>
                    <label className={labelCls}>Nome EN</label>
                    <input required className={inputCls} value={createForm.name_en} onChange={(e) => setCreateForm((f) => ({ ...f, name_en: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t ? t('master:addonsDescPt') : 'Desc PT'}</label>
                    <textarea className={inputCls} rows={2} value={createForm.description_pt} onChange={(e) => setCreateForm((f) => ({ ...f, description_pt: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t ? t('master:addonsDescEn') : 'Desc EN'}</label>
                    <textarea className={inputCls} rows={2} value={createForm.description_en} onChange={(e) => setCreateForm((f) => ({ ...f, description_en: e.target.value }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:monthlyPrice') : 'Mensal'}</label>
                    <input type="number" step="0.01" min={0} className={inputCls} value={createForm.price_brl_monthly} onChange={(e) => setCreateForm((f) => ({ ...f, price_brl_monthly: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:addonsSemiannualShort') : 'Semestral'}</label>
                    <input type="number" step="0.01" min={0} className={inputCls} value={createForm.price_brl_semiannual} onChange={(e) => setCreateForm((f) => ({ ...f, price_brl_semiannual: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:annualPrice') : 'Anual'}</label>
                    <input type="number" step="0.01" min={0} className={inputCls} value={createForm.price_brl_annual} onChange={(e) => setCreateForm((f) => ({ ...f, price_brl_annual: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="md:col-span-2 flex items-end">
                    <button type="submit" disabled={saving} className="py-2 px-6 bg-blue-600 text-white rounded-lg font-bold border-none cursor-pointer disabled:opacity-60">
                        {t ? t('master:create') : '+ Criar'}
                    </button>
                </div>
            </form>

            {editing ? (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={() => setEditing(null)} role="presentation">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 border border-slate-200 dark:border-slate-600" onClick={(e) => e.stopPropagation()} role="dialog">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4">{t ? t('master:addonsEdit') : 'Editar'}</h3>
                        <div className="space-y-3">
                            <div>
                                <label className={labelCls}>{t ? t('master:addonsTableGrant') : 'Tipo'}</label>
                                <select className={inputCls} value={editing.grant_type} onChange={(e) => setEditing((x) => ({ ...x, grant_type: e.target.value }))}>
                                    {ADDON_GRANT_KEYS.map((k) => (
                                        <option key={k} value={k}>{addonGrantMasterLabel(t, k)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:addonsTableQty') : 'Qtd'}</label>
                                <input type="number" min={1} className={inputCls} value={editing.grant_quantity} onChange={(e) => setEditing((x) => ({ ...x, grant_quantity: parseInt(e.target.value, 10) || 1 }))} />
                            </div>
                            <div>
                                <label className={labelCls}>Nome PT</label>
                                <input className={inputCls} value={editing.name_pt} onChange={(e) => setEditing((x) => ({ ...x, name_pt: e.target.value }))} />
                            </div>
                            <div>
                                <label className={labelCls}>Nome EN</label>
                                <input className={inputCls} value={editing.name_en} onChange={(e) => setEditing((x) => ({ ...x, name_en: e.target.value }))} />
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:addonsDescPt') : 'Desc PT'}</label>
                                <textarea className={inputCls} rows={2} value={editing.description_pt || ''} onChange={(e) => setEditing((x) => ({ ...x, description_pt: e.target.value }))} />
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:addonsDescEn') : 'Desc EN'}</label>
                                <textarea className={inputCls} rows={2} value={editing.description_en || ''} onChange={(e) => setEditing((x) => ({ ...x, description_en: e.target.value }))} />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className={labelCls}>R$ /mês</label>
                                    <input type="number" step="0.01" className={inputCls} value={editing.price_brl_monthly} onChange={(e) => setEditing((x) => ({ ...x, price_brl_monthly: parseFloat(e.target.value) || 0 }))} />
                                </div>
                                <div>
                                    <label className={labelCls}>R$ /sem</label>
                                    <input type="number" step="0.01" className={inputCls} value={editing.price_brl_semiannual} onChange={(e) => setEditing((x) => ({ ...x, price_brl_semiannual: parseFloat(e.target.value) || 0 }))} />
                                </div>
                                <div>
                                    <label className={labelCls}>R$ /ano</label>
                                    <input type="number" step="0.01" className={inputCls} value={editing.price_brl_annual} onChange={(e) => setEditing((x) => ({ ...x, price_brl_annual: parseFloat(e.target.value) || 0 }))} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:addonsTableOrder') : 'Ordem'}</label>
                                <input type="number" className={inputCls} value={editing.sort_order} onChange={(e) => setEditing((x) => ({ ...x, sort_order: parseInt(e.target.value, 10) || 0 }))} />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing((x) => ({ ...x, is_active: e.target.checked ? 1 : 0 }))} />
                                {t ? t('master:addonsTableActive') : 'Ativo'}
                            </label>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 font-semibold text-slate-800 dark:text-slate-200">
                                {t ? t('master:cancel') : 'Cancelar'}
                            </button>
                            <button type="button" onClick={saveEdit} disabled={saving} className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-bold disabled:opacity-60 border-none cursor-pointer">
                                {saving ? '…' : (t ? t('master:save') : 'Salvar')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const PlanCouponsMasterView = ({ plans, t }) => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [moodleCouponPrefix, setMoodleCouponPrefix] = useState('');
    const [savingPrefix, setSavingPrefix] = useState(false);
    const [createForm, setCreateForm] = useState({
        code: '',
        discount_percent: 10,
        plan_id: '',
        max_uses: '',
        valid_from: '',
        valid_until: '',
        internal_note: '',
    });

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${API}/master/plan-coupons`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                setCoupons(d.coupons || []);
                setMoodleCouponPrefix(d.coupon_moodle_student_prefix || '');
            })
            .catch(() => {
                setCoupons([]);
                setMoodleCouponPrefix('');
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';

    const saveEdit = async () => {
        if (!editing) return;
        setSaving(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/plan-coupons/${editing.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    code: editing.code,
                    discount_percent: editing.discount_percent,
                    plan_id: editing.plan_id || null,
                    max_uses: editing.max_uses === '' || editing.max_uses == null ? null : editing.max_uses,
                    valid_from: editing.valid_from || null,
                    valid_until: editing.valid_until || null,
                    is_active: editing.is_active ? 1 : 0,
                    internal_note: editing.internal_note || null,
                }),
            });
            const d = await r.json();
            if (!r.ok) setMsg('❌ ' + (d.error || 'Erro'));
            else {
                setMsg('✅ Salvo.');
                setEditing(null);
                load();
            }
        } catch (_) {
            setMsg('❌ Erro de rede.');
        }
        setSaving(false);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/plan-coupons`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    code: createForm.code,
                    discount_percent: createForm.discount_percent,
                    plan_id: createForm.plan_id || null,
                    max_uses: createForm.max_uses === '' ? null : createForm.max_uses,
                    valid_from: createForm.valid_from || null,
                    valid_until: createForm.valid_until || null,
                    internal_note: createForm.internal_note || null,
                }),
            });
            const d = await r.json();
            if (!r.ok) setMsg('❌ ' + (d.error || 'Erro'));
            else {
                setMsg('✅ Cupom criado.');
                setCreateForm({
                    code: '',
                    discount_percent: 10,
                    plan_id: '',
                    max_uses: '',
                    valid_from: '',
                    valid_until: '',
                    internal_note: '',
                });
                load();
            }
        } catch (_) {
            setMsg('❌ Erro de rede.');
        }
        setSaving(false);
    };

    const deactivate = async (id) => {
        if (!window.confirm(t ? t('master:couponsDeactivateConfirm') : 'Desativar este cupom?')) return;
        await fetch(`${API}/master/plan-coupons/${id}`, { method: 'DELETE', headers: authHeaders() });
        load();
    };

    const saveMoodlePrefixRule = async () => {
        setSavingPrefix(true);
        setMsg('');
        try {
            const r = await fetch(`${API}/master/settings`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ plan_coupon_moodle_student_prefix: moodleCouponPrefix }),
            });
            const d = await r.json();
            if (!r.ok) setMsg('❌ ' + (d.error || 'Erro ao salvar regra.'));
            else setMsg('✅ ' + (t ? t('master:couponMoodlePrefixSaved') : 'Regra de cupom salva.'));
        } catch {
            setMsg('❌ Erro de rede.');
        }
        setSavingPrefix(false);
    };

    return (
        <div>
            <h2 className="m-0 mb-2 text-slate-800 dark:text-slate-200">{t ? t('master:couponsMasterTitle') : 'Cupons'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-3xl">{t ? t('master:couponsMasterIntro') : ''}</p>

            <div className="mb-8 rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/50">
                <h3 className="m-0 mb-2 text-sm font-bold text-slate-800 dark:text-slate-200">
                    {t ? t('master:couponMoodlePrefixTitle') : 'Cupom e nome do aluno no Moodle'}
                </h3>
                <p className="mb-3 text-xs text-slate-600 dark:text-slate-400 max-w-3xl">
                    {t ? t('master:couponMoodlePrefixHelp') : ''}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[200px] flex-1">
                        <label className={labelCls}>{t ? t('master:couponMoodlePrefixLabel') : 'Prefixo do código do cupom'}</label>
                        <input
                            type="text"
                            className={inputCls}
                            value={moodleCouponPrefix}
                            onChange={(e) => setMoodleCouponPrefix(e.target.value.toUpperCase())}
                            placeholder={t ? t('master:couponMoodlePrefixPlaceholder') : 'EXPERT_'}
                            maxLength={64}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={saveMoodlePrefixRule}
                        disabled={savingPrefix}
                        className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-white disabled:opacity-60 dark:bg-slate-600"
                    >
                        {savingPrefix ? '…' : (t ? t('master:couponMoodlePrefixSave') : 'Salvar regra')}
                    </button>
                </div>
            </div>

            {msg ? <div className={`p-3 rounded-lg font-semibold mb-4 ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div> : null}
            {loading ? (
                <p className="text-slate-500 dark:text-slate-400">{t ? t('master:loading') : '…'}</p>
            ) : (
                <div className="space-y-3 mb-10">
                    {coupons.map((c) => (
                        <div key={c.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700 flex flex-wrap gap-4 items-center justify-between">
                            <div className="min-w-[200px]">
                                <div className="font-mono font-bold text-slate-800 dark:text-slate-200">{c.code}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {parseFloat(c.discount_percent || 0)}% · {c.plan_id ? (c.plan_name || c.plan_id) : (t ? t('master:couponsAnyPlan') : 'Qualquer plano')}
                                </div>
                                <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">
                                    {t
                                        ? t('master:couponsUsedLabel', {
                                              used: c.used_count || 0,
                                              max: c.max_uses != null ? String(c.max_uses) : '∞',
                                          })
                                        : `Usos: ${c.used_count || 0} / ${c.max_uses != null ? c.max_uses : '∞'}`}
                                </div>
                            </div>
                            <div className="flex gap-2 items-center">
                                <span className="text-sm" title={c.is_active ? 'Ativo' : 'Inativo'}>{c.is_active ? '✅' : '🔴'}</span>
                                <button type="button" onClick={() => setEditing({ ...c, is_active: c.is_active ? 1 : 0, plan_id: c.plan_id || '', max_uses: c.max_uses != null ? c.max_uses : '', valid_from: c.valid_from ? String(c.valid_from).slice(0, 10) : '', valid_until: c.valid_until ? String(c.valid_until).slice(0, 10) : '' })} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold border-none cursor-pointer">
                                    {t ? t('master:addonsEdit') : 'Editar'}
                                </button>
                                <button type="button" onClick={() => deactivate(c.id)} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 rounded-lg text-sm font-bold border-none cursor-pointer">
                                    {t ? t('master:addonsDeactivate') : 'Desativar'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <h3 className="text-slate-800 dark:text-slate-200 mb-4">{t ? t('master:couponsCreateTitle') : 'Novo cupom'}</h3>
            <form onSubmit={handleCreate} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className={labelCls}>{t ? t('master:couponsCode') : 'Código'}</label>
                    <input required className={inputCls} value={createForm.code} onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value }))} placeholder="PROMO100" />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:couponsPercent') : 'Desconto %'}</label>
                    <input type="number" step="0.01" min={1} max={100} required className={inputCls} value={createForm.discount_percent} onChange={(e) => setCreateForm((f) => ({ ...f, discount_percent: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:couponsPlanScope') : 'Plano (opcional)'}</label>
                    <select className={inputCls} value={createForm.plan_id} onChange={(e) => setCreateForm((f) => ({ ...f, plan_id: e.target.value }))}>
                        <option value="">{t ? t('master:couponsAnyPlan') : 'Qualquer plano'}</option>
                        {(plans || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:couponsMaxUses') : 'Máx. usos (vazio = ∞)'}</label>
                    <input type="number" min={1} className={inputCls} value={createForm.max_uses} onChange={(e) => setCreateForm((f) => ({ ...f, max_uses: e.target.value }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:couponsValidFrom') : 'Válido de'}</label>
                    <input type="date" className={inputCls} value={createForm.valid_from} onChange={(e) => setCreateForm((f) => ({ ...f, valid_from: e.target.value }))} />
                </div>
                <div>
                    <label className={labelCls}>{t ? t('master:couponsValidUntil') : 'Válido até'}</label>
                    <input type="date" className={inputCls} value={createForm.valid_until} onChange={(e) => setCreateForm((f) => ({ ...f, valid_until: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls}>{t ? t('master:couponsNote') : 'Nota interna'}</label>
                    <input className={inputCls} value={createForm.internal_note} onChange={(e) => setCreateForm((f) => ({ ...f, internal_note: e.target.value }))} />
                </div>
                <div className="md:col-span-2 flex items-end">
                    <button type="submit" disabled={saving} className="py-2 px-6 bg-blue-600 text-white rounded-lg font-bold border-none cursor-pointer disabled:opacity-60">
                        {t ? t('master:create') : '+ Criar'}
                    </button>
                </div>
            </form>

            {editing ? (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={() => setEditing(null)} role="presentation">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 border border-slate-200 dark:border-slate-600" onClick={(e) => e.stopPropagation()} role="dialog">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4">{t ? t('master:addonsEdit') : 'Editar'}</h3>
                        <div className="space-y-3">
                            <div>
                                <label className={labelCls}>{t ? t('master:couponsCode') : 'Código'}</label>
                                <input className={inputCls} value={editing.code} onChange={(e) => setEditing((x) => ({ ...x, code: e.target.value }))} />
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:couponsPercent') : 'Desconto %'}</label>
                                <input type="number" step="0.01" min={1} max={100} className={inputCls} value={editing.discount_percent} onChange={(e) => setEditing((x) => ({ ...x, discount_percent: parseFloat(e.target.value) || 0 }))} />
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:couponsPlanScope') : 'Plano'}</label>
                                <select className={inputCls} value={editing.plan_id || ''} onChange={(e) => setEditing((x) => ({ ...x, plan_id: e.target.value }))}>
                                    <option value="">{t ? t('master:couponsAnyPlan') : 'Qualquer plano'}</option>
                                    {(plans || []).map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>{t ? t('master:couponsMaxUses') : 'Máx. usos'}</label>
                                <input type="number" min={1} className={inputCls} value={editing.max_uses} onChange={(e) => setEditing((x) => ({ ...x, max_uses: e.target.value }))} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className={labelCls}>{t ? t('master:couponsValidFrom') : 'De'}</label>
                                    <input type="date" className={inputCls} value={editing.valid_from || ''} onChange={(e) => setEditing((x) => ({ ...x, valid_from: e.target.value }))} />
                                </div>
                                <div>
                                    <label className={labelCls}>{t ? t('master:couponsValidUntil') : 'Até'}</label>
                                    <input type="date" className={inputCls} value={editing.valid_until || ''} onChange={(e) => setEditing((x) => ({ ...x, valid_until: e.target.value }))} />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing((x) => ({ ...x, is_active: e.target.checked ? 1 : 0 }))} />
                                {t ? t('master:addonsTableActive') : 'Ativo'}
                            </label>
                            <div>
                                <label className={labelCls}>{t ? t('master:couponsNote') : 'Nota'}</label>
                                <input className={inputCls} value={editing.internal_note || ''} onChange={(e) => setEditing((x) => ({ ...x, internal_note: e.target.value }))} />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 font-semibold text-slate-800 dark:text-slate-200">
                                {t ? t('master:cancel') : 'Cancelar'}
                            </button>
                            <button type="button" onClick={saveEdit} disabled={saving} className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-bold disabled:opacity-60 border-none cursor-pointer">
                                {saving ? '…' : (t ? t('master:save') : 'Salvar')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const PlansArea = ({ plans, onRefresh, t }) => {
    const [sub, setSub] = useState('catalog');
    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 dark:border-slate-700 pb-4">
                <button
                    type="button"
                    onClick={() => setSub('catalog')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold border-none cursor-pointer transition-colors ${sub === 'catalog' ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:opacity-90'}`}
                >
                    {t ? t('master:plansTabCatalog') : 'Planos'}
                </button>
                <button
                    type="button"
                    onClick={() => setSub('addons')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold border-none cursor-pointer transition-colors ${sub === 'addons' ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:opacity-90'}`}
                >
                    {t ? t('master:plansTabAddons') : 'Add-ons'}
                </button>
                <button
                    type="button"
                    onClick={() => setSub('coupons')}
                    className={`px-4 py-2 rounded-lg text-sm font-bold border-none cursor-pointer transition-colors ${sub === 'coupons' ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:opacity-90'}`}
                >
                    {t ? t('master:plansTabCoupons') : 'Cupons'}
                </button>
            </div>
            {sub === 'catalog' ? <PlansView plans={plans} onRefresh={onRefresh} t={t} /> : sub === 'addons' ? <PlanAddonsMasterView t={t} /> : <PlanCouponsMasterView plans={plans} t={t} />}
        </div>
    );
};

const PlansView = ({ plans, onRefresh, t }) => {
    const [form, setForm] = useState({ name: '', price_brl: 0, show_on_profile: true });
    const [editingPlan, setEditingPlan] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        const r = await fetch(`${API}/master/plans`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
                name: form.name,
                price_brl: form.price_brl,
                show_on_profile: form.show_on_profile ? 1 : 0,
                max_projects: 9999,
                max_users: 1,
                features: {
                    grids: ["1/1", "1/2"],
                    elements: ["text"],
                    modules: {},
                    save: 0,
                    max_collaborators_editor: 0,
                    max_collaborators_viewer: 0
                }
            })
        });
        const d = await r.json();
        setMsg(r.ok ? '✅ Plano criado!' : '❌ ' + d.error);
        if (r.ok) { setForm({ name: '', price_brl: 0, show_on_profile: true }); onRefresh(); }
        setSaving(false);
    };

    const handleUpdate = async (id, data) => {
        try {
            const r = await fetch(`${API}/master/plans/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) });
            const d = await r.json();
            if (!r.ok) {
                setMsg('❌ ' + (d.error || 'Erro ao salvar.'));
                return false;
            }
            setMsg('✅ Configurações salvas com sucesso!');
            onRefresh();
            return true;
        } catch (err) {
            setMsg('❌ Erro de rede ao salvar.');
            return false;
        }
    };

    const togglePlan = async (e, plan) => {
        e.stopPropagation();
        await handleUpdate(plan.id, { is_active: plan.is_active ? 0 : 1 });
    };

    const toggleShowOnProfile = async (e, plan) => {
        e.stopPropagation();
        const next = plan.show_on_profile === 0 || plan.show_on_profile === '0' ? 1 : 0;
        await handleUpdate(plan.id, { show_on_profile: next });
    };

    const inputCls = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1';

    return (
        <div>
            <h2 className="m-0 mb-6 text-slate-800 dark:text-slate-200">💎 Planos</h2>
            <p className="-mt-2 mb-6 text-sm text-slate-500 dark:text-slate-400">
                {t ? t('master:plansIntroConfigure') : (
                    <>
                        Clique em <strong>Definir recursos do plano</strong> em cada card para definir o <strong>máximo de projetos salvos no palco</strong>, colaboradores, armazenamento (MB), grids, elementos e módulos.
                    </>
                )}
            </p>
            {msg && <div className={`p-3 rounded-lg font-semibold mb-4 ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {plans.map(p => (
                    <div
                        key={p.id}
                        className={`bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm flex flex-col transition-transform hover:scale-[1.02] border-2 ${p.is_active ? 'border-blue-500 dark:border-blue-500 opacity-100' : 'border-slate-200 dark:border-slate-600 opacity-60'}`}
                    >
                        <div className="flex justify-between items-start mb-2 gap-2">
                            <h3 className="m-0 text-slate-800 dark:text-slate-200">{p.name}</h3>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    type="button"
                                    title={t ? t('master:planProfileVisibilityToggleTitle') : 'Alternar visibilidade no perfil'}
                                    onClick={(e) => toggleShowOnProfile(e, p)}
                                    className="bg-transparent border-none cursor-pointer text-base leading-none px-1"
                                >
                                    {(p.show_on_profile === 0 || p.show_on_profile === '0') ? '👁️‍🗨️' : '👁️'}
                                </button>
                                <button type="button" onClick={(e) => togglePlan(e, p)} className="bg-transparent border-none cursor-pointer text-base">{p.is_active ? '✅' : '🔴'}</button>
                            </div>
                        </div>
                        {(p.show_on_profile === 0 || p.show_on_profile === '0') && (
                            <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2">
                                {t ? t('master:planCardProfileHiddenBadge') : 'Oculto no perfil'}
                            </div>
                        )}
                        {(p.is_admin_plan === 1 || p.is_admin_plan === '1') && (
                            <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300 mb-2">
                                {t ? t('master:planCardAdminBadge') : 'Plano administrativo (fora do MRR)'}
                            </div>
                        )}
                        <div className="text-2xl font-extrabold text-blue-600 dark:text-blue-400 mb-2">
                            {(() => {
                                const brlMo = parseFloat(p.price_brl_monthly ?? p.price_brl ?? 0) || 0;
                                const brlYr = parseFloat(p.price_brl_annual ?? 0) || 0;
                                if (brlMo <= 0 && brlYr <= 0) return 'Grátis';
                                if (brlMo > 0) {
                                    return (
                                        <>
                                            R$ {brlMo.toFixed(2)}
                                            <span className="text-sm text-slate-400 dark:text-slate-500 font-normal">/mês</span>
                                            {brlYr > 0 ? (
                                                <div className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                    ou R$ {brlYr.toFixed(2)}/ano
                                                </div>
                                            ) : null}
                                        </>
                                    );
                                }
                                return (
                                    <>
                                        R$ {brlYr.toFixed(2)}
                                        <span className="text-sm text-slate-400 dark:text-slate-500 font-normal">/ano</span>
                                    </>
                                );
                            })()}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                            <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                {t ? (() => {
                                    const lim = getPlanSaveSlotLimit(p.features || {});
                                    const countLabel = !Number.isFinite(lim) ? t('master:storageUnlimitedSym') : String(lim);
                                    return t('master:planCardSaveSlots', { count: countLabel });
                                })() : (
                                    <>💾 Palco (nuvem): até {(() => {
                                        const lim = getPlanSaveSlotLimit(p.features || {});
                                        return !Number.isFinite(lim) ? '∞' : String(lim);
                                    })()} projeto(s)</>
                                )}
                            </div>
                            {(p.features?.max_collaborators_editor != null || p.features?.max_collaborators_viewer != null) && (
                                <div className="text-xs mt-1.5">
                                    ✏️ Ed.: {p.features?.max_collaborators_editor ?? 0} · 👁️ Ver.: {p.features?.max_collaborators_viewer ?? 0}
                                </div>
                            )}
                            <div className={`text-[10px] leading-tight text-slate-500 dark:text-slate-400 ${(p.features?.max_collaborators_editor != null || p.features?.max_collaborators_viewer != null) ? 'mt-2' : ''}`}>
                                {t ? t('master:storageCardMbLine', {
                                    p: p.storage_projects_mb ?? t('master:storageUnlimitedSym'),
                                    l: p.storage_logs_mb ?? t('master:storageUnlimitedSym'),
                                    m: p.storage_media_mb ?? t('master:storageUnlimitedSym'),
                                }) : `💾 MB — Proj.: ${p.storage_projects_mb ?? '∞'} · Logs: ${p.storage_logs_mb ?? '∞'} · Mídias: ${p.storage_media_mb ?? '∞'}`}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setMsg(''); setEditingPlan(p); }}
                            className="mt-auto py-2.5 px-4 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-bold text-sm w-full"
                        >
                            ⚙️ {t ? t('master:definePlanResourcesButton') : 'Definir recursos do plano'}
                        </button>
                    </div>
                ))}
            </div>

            {editingPlan && (
                <PlanConfigModal
                    key={editingPlan.id}
                    plan={editingPlan}
                    onClose={() => setEditingPlan(null)}
                    onSave={handleUpdate}
                />
            )}

            <h3 className="text-slate-800 dark:text-slate-200 mb-4">Criar Novo Plano</h3>
            <form onSubmit={handleCreate} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-end border border-slate-200 dark:border-slate-700">
                {[['Nome', 'name', 'text', 'Ex: Pro'], ['Preço (R$)', 'price_brl', 'number', '0']].map(([label, key, type, ph]) => (
                    <div key={key}>
                        <label className={labelCls}>{label}</label>
                        <input required={key === 'name'} type={type} step={key === 'price_brl' ? '0.01' : '1'} min="0" value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: type === 'number' ? parseFloat(e.target.value) : e.target.value }))} placeholder={ph} className={inputCls} />
                    </div>
                ))}
                <label className="flex items-center gap-2 cursor-pointer pb-2 md:pb-0">
                    <input type="checkbox" checked={!!form.show_on_profile} onChange={(e) => setForm((p) => ({ ...p, show_on_profile: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{t ? t('master:planShowOnProfileShort') : 'Exibir no perfil'}</span>
                </label>
                <button type="submit" disabled={saving} className="py-2 px-5 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-bold whitespace-nowrap disabled:opacity-60">
                    {saving ? '...' : (t ? t('master:create') : '+ Criar')}
                </button>
            </form>

            <FeatureFlagsManager />
        </div>
    );
};

// ─── ADMINISTRADORES ──────────────────────────────────────────
const AdminsView = () => {
    const [admins, setAdmins] = useState([]);
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [msg, setMsg] = useState('');

    const load = () => fetch(`${API}/master/admins`, { headers: authHeaders() }).then(r => r.json()).then(d => setAdmins(d.admins || []));
    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        const r = await fetch(`${API}/master/admins`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
        const d = await r.json();
        setMsg(r.ok ? '✅ Admin criado!' : '❌ ' + d.error);
        if (r.ok) { setForm({ name: '', email: '', password: '' }); load(); }
    };

    return (
        <div>
            <h2 className="m-0 mb-6 text-slate-800 dark:text-slate-200">🛡️ Administradores</h2>
            {msg && <div className={`p-3 rounded-lg font-semibold mb-4 ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>{msg}</div>}

            <form onSubmit={handleCreate} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end border border-slate-200 dark:border-slate-700">
                {[['Nome', 'name', 'text'], ['Email', 'email', 'email'], ['Senha', 'password', 'password']].map(([label, key, type]) => (
                    <div key={key}>
                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">{label}</label>
                        <input required type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                            className="w-full px-2.5 py-2 border border-slate-200 dark:border-slate-600 rounded-md text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800" />
                    </div>
                ))}
                <button type="submit" className="py-2 px-5 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-bold">+ Adicionar</button>
            </form>

            <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700">
                {admins.map((a, i) => (
                    <div key={a.id} className={`p-4 px-5 flex items-center gap-4 ${i < admins.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''}`}>
                        <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                            {a.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                            <div className="font-bold text-slate-800 dark:text-slate-200">{a.name}</div>
                            <div className="text-sm text-slate-400 dark:text-slate-500">{a.email}</div>
                        </div>
                        <Badge status="active" />
                        <div className="text-xs text-slate-400 dark:text-slate-500">Desde {new Date(a.created_at).toLocaleDateString('pt-BR')}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── RELATÓRIOS ───────────────────────────────────────────────
const fmtReportDate = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');
const fmtReportMoney = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};

/** Coluna única: código(s) do cupom e valor do desconto (BRL) concedido sem passar pelo Asaas. */
const fmtReportCouponCell = (codes, discountBrl) => {
    const d = Number(discountBrl);
    const hasDisc = Number.isFinite(d) && d > 0;
    const c = (codes || '').trim();
    if (!c && !hasDisc) return '—';
    if (c && hasDisc) return `${c} (−${fmtReportMoney(d)})`;
    if (c) return c;
    return `−${fmtReportMoney(d)}`;
};

const FEEDBACK_STATUS_OPTIONS = [
    { value: 'received', label: 'Recebido' },
    { value: 'in_progress', label: 'Em andamento' },
    { value: 'done', label: 'Concluído' },
];

const UserFeedbackMasterView = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [ratingFilter, setRatingFilter] = useState('');
    const [openId, setOpenId] = useState(null);
    const [draft, setDraft] = useState({ status: 'received', admin_reply: '' });
    const [savingId, setSavingId] = useState(null);
    const [saveMsg, setSaveMsg] = useState(null);
    const limit = 25;

    const load = useCallback(() => {
        setLoading(true);
        const qs = new URLSearchParams({
            page: String(page),
            limit: String(limit),
            ...(ratingFilter ? { rating: ratingFilter } : {}),
        });
        fetch(`${API}/master/feedback?${qs}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                if (d.error) {
                    setRows([]);
                    setTotal(0);
                } else {
                    setRows(d.rows || []);
                    setTotal(d.total ?? 0);
                }
                setLoading(false);
            })
            .catch(() => {
                setRows([]);
                setTotal(0);
                setLoading(false);
            });
    }, [page, ratingFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const exportCsv = () => {
        const header = ['Data', 'Nota', 'Status', 'Contexto', 'Projeto (layout_id)', 'Comentário', 'Resposta', 'Usuário', 'E-mail', 'Cliente', 'Slug tenant', 'tenant_id'];
        const lines = [header.join(';')];
        (rows || []).forEach((row) => {
            const dt = row.created_at ? new Date(row.created_at).toLocaleString('pt-BR') : '';
            const comment = (row.comment || '').replace(/\r\n/g, '\n');
            const reply = (row.admin_reply || '').replace(/\r\n/g, '\n');
            lines.push(
                [
                    dt,
                    row.rating,
                    row.status || 'received',
                    row.page_context || '—',
                    row.layout_id || '—',
                    comment,
                    reply,
                    row.user_name || '—',
                    row.user_email || '—',
                    row.tenant_name || '—',
                    row.tenant_slug || '—',
                    row.tenant_id || '—',
                ]
                    .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`)
                    .join(';')
            );
        });
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `feedback-usuarios-p${page}.csv`;
        a.click();
    };

    const toggleRow = (row) => {
        if (openId === row.id) {
            setOpenId(null);
            return;
        }
        setOpenId(row.id);
        setDraft({ status: row.status || 'received', admin_reply: row.admin_reply || '' });
        setSaveMsg(null);
    };

    const saveReply = async (id) => {
        setSavingId(id);
        setSaveMsg(null);
        try {
            const r = await fetch(`${API}/master/feedback/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify(draft),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) {
                setSaveMsg(d.error || 'Não foi possível salvar.');
                setSavingId(null);
                return;
            }
            setRows((prev) => prev.map((row) => (row.id === id ? { ...row, status: draft.status, admin_reply: draft.admin_reply } : row)));
            setSaveMsg(d.notified ? 'Salvo. O usuário verá uma nova mensagem no painel.' : 'Salvo.');
        } catch {
            setSaveMsg('Erro de conexão.');
        }
        setSavingId(null);
    };

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const statusLabel = (value) => FEEDBACK_STATUS_OPTIONS.find((opt) => opt.value === value)?.label || 'Recebido';

    return (
        <div>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="m-0 text-slate-800 dark:text-slate-200">📝 Feedback dos usuários</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Mude o status e escreva a resposta. O usuário vê o texto e um aviso de nova mensagem no botão de feedback.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                        Nota
                        <select
                            value={ratingFilter}
                            onChange={(e) => {
                                setPage(1);
                                setRatingFilter(e.target.value);
                            }}
                            className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900"
                        >
                            <option value="">Todas</option>
                            {[5, 4, 3, 2, 1].map((n) => (
                                <option key={n} value={String(n)}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        onClick={exportCsv}
                        disabled={!rows.length}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                        Exportar CSV (página)
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-slate-500">Carregando…</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <table className="w-full min-w-[860px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-900/50">
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Data</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Nota</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Onde</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Usuário</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Comentário</th>
                                <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                                        Nenhum feedback ainda.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <tr className="border-b border-slate-100 dark:border-slate-700/80">
                                            <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">
                                                {row.created_at ? new Date(row.created_at).toLocaleString('pt-BR') : '—'}
                                            </td>
                                            <td className="px-3 py-2 font-bold text-blue-700 dark:text-blue-400">{row.rating}</td>
                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{statusLabel(row.status)}</td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                                {row.page_context || '—'}
                                                {row.layout_id ? (
                                                    <span className="mt-0.5 block text-xs text-slate-400">proj. {row.layout_id.slice(0, 12)}…</span>
                                                ) : null}
                                            </td>
                                            <td className="max-w-[180px] px-3 py-2 text-slate-700 dark:text-slate-300">
                                                <div className="truncate font-medium">{row.user_name || '—'}</div>
                                                <div className="truncate text-xs text-slate-500">{row.user_email || ''}</div>
                                            </td>
                                            <td className="max-w-md px-3 py-2 text-slate-700 dark:text-slate-300">
                                                <span className="line-clamp-3 whitespace-pre-wrap break-words">{row.comment || '—'}</span>
                                                {Array.isArray(row.images) && row.images.length > 0 ? (
                                                    <span className="mt-1 block text-xs text-slate-400">{row.images.length} print(s)</span>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRow(row)}
                                                    className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                                                >
                                                    {openId === row.id ? 'Fechar' : 'Responder'}
                                                </button>
                                            </td>
                                        </tr>
                                        {openId === row.id && (
                                            <tr className="border-b border-slate-100 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-900/40">
                                                <td colSpan={7} className="px-3 py-3">
                                                    {Array.isArray(row.images) && row.images.length > 0 && (
                                                        <div className="mb-3 flex flex-wrap gap-2">
                                                            {row.images.map((url) => (
                                                                <a key={url} href={uploadsPublicUrl(url)} target="_blank" rel="noreferrer">
                                                                    <img src={uploadsPublicUrl(url)} alt="Print do feedback" className="h-20 w-20 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-600" />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                                                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                                                            Status
                                                            <select
                                                                value={draft.status}
                                                                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                                                                className="mt-1 block rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900"
                                                            >
                                                                {FEEDBACK_STATUS_OPTIONS.map((opt) => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                        <label className="min-w-0 flex-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                                                            Resposta para o usuário
                                                            <textarea
                                                                value={draft.admin_reply}
                                                                onChange={(e) => setDraft((d) => ({ ...d, admin_reply: e.target.value }))}
                                                                rows={3}
                                                                maxLength={4000}
                                                                placeholder="Obrigado, a correção já está disponível."
                                                                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-normal dark:border-slate-600 dark:bg-slate-900"
                                                            />
                                                        </label>
                                                        <button
                                                            type="button"
                                                            disabled={savingId === row.id}
                                                            onClick={() => saveReply(row.id)}
                                                            className="mt-5 shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                                        >
                                                            {savingId === row.id ? 'Salvando…' : 'Salvar'}
                                                        </button>
                                                    </div>
                                                    {saveMsg && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">{saveMsg}</p>}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {total > 0 && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-600 dark:text-slate-400">
                    <span>
                        Total: {total} — página {page} de {totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40 dark:border-slate-600"
                        >
                            Anterior
                        </button>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40 dark:border-slate-600"
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const fmtActivityDuration = (sec) => {
    if (sec == null || !Number.isFinite(Number(sec))) return '—';
    const s = Math.max(0, Math.floor(Number(sec)));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${r}s`;
    return `${r}s`;
};

const ReportsView = () => {
    const { t } = useTranslation('master');
    const [tab, setTab] = useState('subscriptions');
    const [q, setQ] = useState('');
    const [qDebounced, setQDebounced] = useState('');
    const [moodleStudentFilter, setMoodleStudentFilter] = useState('');
    const [moodleStudentFilterDebounced, setMoodleStudentFilterDebounced] = useState('');
    const [moodlePresentOnly, setMoodlePresentOnly] = useState(false);
    const [statusSub, setStatusSub] = useState('');
    const [statusAddon, setStatusAddon] = useState('');
    const [pageSub, setPageSub] = useState(1);
    const [pageAddon, setPageAddon] = useState(1);
    const limit = 20;

    const [subState, setSubState] = useState({ rows: [], total: 0, loading: true });
    const [addonState, setAddonState] = useState({ rows: [], total: 0, loading: true });
    const [activityDays, setActivityDays] = useState(30);
    const [activityState, setActivityState] = useState({ loading: false, data: null });

    useEffect(() => {
        const tm = setTimeout(() => setQDebounced(q), 350);
        return () => clearTimeout(tm);
    }, [q]);

    useEffect(() => {
        const tm = setTimeout(() => setMoodleStudentFilterDebounced(moodleStudentFilter), 350);
        return () => clearTimeout(tm);
    }, [moodleStudentFilter]);

    useEffect(() => { setPageSub(1); }, [qDebounced, statusSub, moodleStudentFilterDebounced, moodlePresentOnly]);
    useEffect(() => { setPageAddon(1); }, [qDebounced, statusAddon]);

    useEffect(() => {
        if (tab !== 'userActivity') return;
        setActivityState((s) => ({ ...s, loading: true }));
        fetch(`${API}/master/reports/user-activity?days=${activityDays}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                if (d.error) setActivityState({ loading: false, data: null });
                else setActivityState({ loading: false, data: d });
            })
            .catch(() => setActivityState({ loading: false, data: null }));
    }, [tab, activityDays]);

    useEffect(() => {
        if (tab !== 'subscriptions') return;
        setSubState((s) => ({ ...s, loading: true }));
        const qs = new URLSearchParams({
            page: String(pageSub),
            limit: String(limit),
            ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
            ...(statusSub ? { status: statusSub } : {}),
            ...(moodleStudentFilterDebounced.trim() ? { moodle_student: moodleStudentFilterDebounced.trim() } : {}),
            ...(moodlePresentOnly ? { moodle_student_present: '1' } : {}),
        });
        fetch(`${API}/master/reports/subscriptions?${qs}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                if (d.error) {
                    setSubState({ rows: [], total: 0, loading: false });
                    return;
                }
                setSubState({ rows: d.rows || [], total: d.total ?? 0, loading: false });
            })
            .catch(() => setSubState({ rows: [], total: 0, loading: false }));
    }, [tab, pageSub, qDebounced, statusSub, moodleStudentFilterDebounced, moodlePresentOnly]);

    useEffect(() => {
        if (tab !== 'addons') return;
        setAddonState((s) => ({ ...s, loading: true }));
        const qs = new URLSearchParams({
            page: String(pageAddon),
            limit: String(limit),
            ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
            ...(statusAddon ? { status: statusAddon } : {}),
        });
        fetch(`${API}/master/reports/addons?${qs}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                if (d.error) {
                    setAddonState({ rows: [], total: 0, loading: false });
                    return;
                }
                setAddonState({ rows: d.rows || [], total: d.total ?? 0, loading: false });
            })
            .catch(() => setAddonState({ rows: [], total: 0, loading: false }));
    }, [tab, pageAddon, qDebounced, statusAddon]);

    const exportSubscriptionsCsv = async () => {
        const qs = new URLSearchParams({
            page: '1',
            limit: '5000',
            ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
            ...(statusSub ? { status: statusSub } : {}),
            ...(moodleStudentFilterDebounced.trim() ? { moodle_student: moodleStudentFilterDebounced.trim() } : {}),
            ...(moodlePresentOnly ? { moodle_student_present: '1' } : {}),
        });
        const r = await fetch(`${API}/master/reports/subscriptions?${qs}`, { headers: authHeaders() });
        const d = await r.json();
        const rows = d.rows || [];
        const header = [
            'Cliente', 'Slug', 'Plano', 'Status', 'Registro', 'Assinatura', 'Últ. pagamento', 'Próx. venc.',
            'Cancelamento', 'Add-on', 'Cupom', 'Nome aluno (Moodle)', 'Total pago (líquido)', 'Valor add-on (no total)',
        ];
        const lines = [header.join(';')];
        rows.forEach((s) => {
            lines.push([
                s.tenant, s.slug, s.plan, s.status,
                fmtReportDate(s.registered_at),
                fmtReportDate(s.subscription_date),
                s.last_payment_at ? fmtReportDate(s.last_payment_at) : '—',
                fmtReportDate(s.next_due_date),
                s.cancelled_at ? fmtReportDate(s.cancelled_at) : '—',
                s.has_addon ? 'Sim' : 'Não',
                fmtReportCouponCell(s.coupon_codes, s.coupon_discount_sum_brl),
                (s.coupon_moodle_student_names || '').trim() || '—',
                fmtReportMoney(s.total_paid_brl),
                fmtReportMoney(s.addon_paid_brl),
            ].map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'));
        });
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'relatorio-assinaturas.csv';
        a.click();
    };

    const exportAddonsCsv = async () => {
        const qs = new URLSearchParams({
            page: '1',
            limit: '5000',
            ...(qDebounced.trim() ? { q: qDebounced.trim() } : {}),
            ...(statusAddon ? { status: statusAddon } : {}),
        });
        const r = await fetch(`${API}/master/reports/addons?${qs}`, { headers: authHeaders() });
        const d = await r.json();
        const rows = d.rows || [];
        const header = ['Cliente', 'Slug', 'Add-on', 'Ciclo', 'Aquisição', 'Próx. venc.', 'Cancelamento', 'Status'];
        const lines = [header.join(';')];
        rows.forEach((s) => {
            lines.push([
                s.tenant, s.slug, s.addon_name, s.billing_cycle || '—',
                fmtReportDate(s.acquired_at),
                fmtReportDate(s.next_due_date),
                s.cancelled_at ? fmtReportDate(s.cancelled_at) : '—',
                s.status,
            ].map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'));
        });
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'relatorio-addons.csv';
        a.click();
    };

    const totalPagesSub = Math.max(1, Math.ceil(subState.total / limit));
    const totalPagesAddon = Math.max(1, Math.ceil(addonState.total / limit));

    const inputClass = 'w-full min-w-[140px] px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const btnTab = (active) =>
        `px-4 py-2 rounded-lg text-sm font-bold border-none cursor-pointer transition-colors ${
            active ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:opacity-90'
        }`;

    return (
        <div>
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h2 className="m-0 text-slate-800 dark:text-slate-200">📈 Relatórios</h2>
                <div className="flex flex-wrap gap-2">
                    {(tab === 'subscriptions' || tab === 'addons') && (
                        <button type="button" onClick={() => (tab === 'subscriptions' ? exportSubscriptionsCsv() : exportAddonsCsv())} className="px-4 py-2 bg-emerald-600 text-white border-none rounded-lg cursor-pointer font-semibold">
                            ⬇ Exportar CSV
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <button type="button" className={btnTab(tab === 'subscriptions')} onClick={() => setTab('subscriptions')}>
                    Assinaturas
                </button>
                <button type="button" className={btnTab(tab === 'addons')} onClick={() => setTab('addons')}>
                    Add-ons por cliente
                </button>
                <button type="button" className={btnTab(tab === 'userActivity')} onClick={() => setTab('userActivity')}>
                    Atividade de usuários
                </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-4 items-end">
                {tab !== 'userActivity' ? (
                <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">Buscar</label>
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={tab === 'subscriptions' ? 'Nome ou slug do cliente…' : 'Cliente, slug ou nome do add-on…'}
                        className={inputClass}
                    />
                </div>
                ) : (
                <div className="flex flex-wrap gap-3 items-end">
                    <div className="min-w-[160px]">
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">Período</label>
                        <select value={activityDays} onChange={(e) => setActivityDays(Number(e.target.value))} className={inputClass}>
                            <option value={7}>Últimos 7 dias</option>
                            <option value={30}>Últimos 30 dias</option>
                            <option value={90}>Últimos 90 dias</option>
                        </select>
                    </div>
                </div>
                )}
                {tab === 'subscriptions' ? (
                    <>
                        <div className="min-w-[160px]">
                            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">Status</label>
                            <select value={statusSub} onChange={(e) => setStatusSub(e.target.value)} className={inputClass}>
                                <option value="">Todos</option>
                                <option value="active">Ativo</option>
                                <option value="trial">Trial</option>
                                <option value="pending">Pendente</option>
                                <option value="overdue">Em atraso</option>
                                <option value="cancelled">Cancelado</option>
                            </select>
                        </div>
                        <div className="min-w-[200px] flex-1 max-w-md">
                            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                                {t('reportsMoodleStudentFilterLabel')}
                            </label>
                            <input
                                type="search"
                                value={moodleStudentFilter}
                                onChange={(e) => setMoodleStudentFilter(e.target.value)}
                                placeholder={t('reportsMoodleStudentFilterPlaceholder')}
                                className={inputClass}
                            />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300 pb-1">
                            <input
                                type="checkbox"
                                checked={moodlePresentOnly}
                                onChange={(e) => setMoodlePresentOnly(e.target.checked)}
                                className="rounded border-slate-300 dark:border-slate-600"
                            />
                            <span>{t('reportsMoodleStudentPresentOnly')}</span>
                        </label>
                    </>
                ) : tab === 'addons' ? (
                    <div className="min-w-[180px]">
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1">Status</label>
                        <select value={statusAddon} onChange={(e) => setStatusAddon(e.target.value)} className={inputClass}>
                            <option value="">Todos</option>
                            <option value="active">Ativo</option>
                            <option value="pending_checkout">Checkout pendente</option>
                            <option value="overdue">Em atraso</option>
                            <option value="cancelled">Cancelado</option>
                        </select>
                    </div>
                ) : null}
            </div>

            {tab === 'userActivity' && (
                <>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                        Logins e projetos free são gravados no servidor. A duração da sessão usa o último ping (a cada ~3 min) ou o logout; se o usuário apenas fechar o navegador, usamos o último ping como fim aproximado.
                    </p>
                    {activityState.loading ? (
                        <div className="p-8 text-center text-slate-400">Carregando…</div>
                    ) : !activityState.data ? (
                        <div className="p-8 text-center text-slate-400">Não foi possível carregar o relatório.</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                                <KpiCard label="Usuários distintos (período)" value={activityState.data.summary?.distinct_users_period} icon="👤" color="#6366f1" />
                                <KpiCard label="Total de logins (período)" value={activityState.data.summary?.total_logins_period} icon="🔑" color="#3b82f6" />
                                <KpiCard label="Logins hoje (todas as sessões)" value={activityState.data.summary?.logins_today} icon="📅" color="#0ea5e9" />
                                <KpiCard label="Usuários únicos hoje" value={activityState.data.summary?.distinct_users_today} icon="✨" color="#10b981" />
                                <KpiCard label="Projetos free criados (período)" value={activityState.data.summary?.free_projects_created_period} icon="📁" color="#f59e0b" sub="Evento enviado pelo app" />
                            </div>

                            <h3 className="text-slate-800 dark:text-slate-200 mb-2 text-base font-semibold">Logins por dia</h3>
                            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto mb-8">
                                <table className="w-full min-w-[400px] text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50">
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">Data</th>
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">Usuários únicos</th>
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">Total de logins</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(activityState.data.dailyLogins || []).length === 0 ? (
                                            <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Sem dados no período.</td></tr>
                                        ) : (
                                            activityState.data.dailyLogins.map((row) => (
                                                <tr key={row.day} className="border-b border-slate-100 dark:border-slate-700/80">
                                                    <td className="px-4 py-2 whitespace-nowrap">{row.day ? new Date(row.day + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                                    <td className="px-4 py-2">{row.distinct_users ?? '—'}</td>
                                                    <td className="px-4 py-2">{row.total_logins ?? '—'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <h3 className="text-slate-800 dark:text-slate-200 mb-2 text-base font-semibold">Quem mais criou projeto local (free)</h3>
                            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto mb-8">
                                <table className="w-full min-w-[420px] text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50">
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">Nome</th>
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">E-mail</th>
                                            <th className="text-left px-4 py-2 font-semibold text-slate-700 dark:text-slate-300">Criações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(activityState.data.topFreeProjectCreators || []).length === 0 ? (
                                            <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Nenhum evento registrado.</td></tr>
                                        ) : (
                                            activityState.data.topFreeProjectCreators.map((row) => (
                                                <tr key={row.user_id} className="border-b border-slate-100 dark:border-slate-700/80">
                                                    <td className="px-4 py-2">{row.name || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{row.email || '—'}</td>
                                                    <td className="px-4 py-2 font-semibold">{row.free_projects_created}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <h3 className="text-slate-800 dark:text-slate-200 mb-2 text-base font-semibold">Sessões recentes</h3>
                            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                <table className="w-full min-w-[760px] text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50">
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Início</th>
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Usuário</th>
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Método</th>
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Último ping</th>
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Encerramento</th>
                                            <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Duração</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(activityState.data.recentSessions || []).length === 0 ? (
                                            <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Sem sessões no período.</td></tr>
                                        ) : (
                                            activityState.data.recentSessions.map((row) => (
                                                <tr key={row.id} className="border-b border-slate-100 dark:border-slate-700/80">
                                                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">{row.started_at ? new Date(row.started_at).toLocaleString('pt-BR') : '—'}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[180px]" title={row.user_email}>{row.user_name || '—'}</div>
                                                        <div className="text-xs text-slate-500 truncate max-w-[200px]" title={row.user_email}>{row.user_email}</div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {row.method === 'master_impersonate'
                                                            ? 'Painel Master'
                                                            : row.method === 'password'
                                                              ? 'E-mail/senha'
                                                              : row.method === 'google'
                                                                ? 'Google'
                                                                : (row.method || '—')}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">{row.last_seen_at ? new Date(row.last_seen_at).toLocaleString('pt-BR') : '—'}</td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">{row.ended_at ? new Date(row.ended_at).toLocaleString('pt-BR') : <span className="text-amber-600 dark:text-amber-400 font-medium">Aberta</span>}</td>
                                                    <td className="px-3 py-2">{fmtActivityDuration(row.effective_seconds)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}

            {tab === 'subscriptions' && (
                <>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Total pago (líquido) = soma das cobranças confirmadas (Asaas) menos descontos de cupom registrados em BRL (ex.: ativação 100% sem gateway). Último pagamento e valores brutos vêm do registro em banco; add-ons entram na soma bruta.
                    </p>
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                        <div
                            className="grid gap-2 p-3 px-4 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide min-w-[1380px]"
                            style={{ gridTemplateColumns: 'minmax(120px,1.2fr) minmax(72px,0.7fr) minmax(88px,0.8fr) 72px minmax(78px,0.75fr) minmax(78px,0.75fr) minmax(88px,0.85fr) minmax(78px,0.75fr) minmax(78px,0.75fr) 52px minmax(100px,1fr) minmax(110px,1.05fr) minmax(88px,0.9fr) minmax(88px,0.85fr)' }}
                        >
                            <span>Cliente</span>
                            <span>Slug</span>
                            <span>Plano</span>
                            <span>St.</span>
                            <span>Registro</span>
                            <span>Assinatura</span>
                            <span>Últ. pagto</span>
                            <span>Próx. venc.</span>
                            <span>Cancel.</span>
                            <span>Add-on</span>
                            <span>Cupom</span>
                            <span>{t('reportsMoodleStudentCol')}</span>
                            <span>Total pago</span>
                            <span>Parte add-on</span>
                        </div>
                        {subState.loading ? (
                            <div className="p-8 text-center text-slate-400">Carregando…</div>
                        ) : subState.rows.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 dark:text-slate-500">Nenhum registro.</div>
                        ) : (
                            subState.rows.map((s) => (
                                <div
                                    key={s.subscription_id || `${s.tenant_id}-${s.slug}`}
                                    className="grid gap-2 p-3 px-4 border-b border-slate-100 dark:border-slate-700 text-xs items-center min-w-[1380px] text-slate-700 dark:text-slate-300"
                                    style={{ gridTemplateColumns: 'minmax(120px,1.2fr) minmax(72px,0.7fr) minmax(88px,0.8fr) 72px minmax(78px,0.75fr) minmax(78px,0.75fr) minmax(88px,0.85fr) minmax(78px,0.75fr) minmax(78px,0.75fr) 52px minmax(100px,1fr) minmax(110px,1.05fr) minmax(88px,0.9fr) minmax(88px,0.85fr)' }}
                                >
                                    <span className="font-semibold text-slate-800 dark:text-slate-200">{s.tenant}</span>
                                    <span className="text-slate-500 truncate" title={s.slug}>{s.slug}</span>
                                    <span className="truncate" title={s.plan}>{s.plan}</span>
                                    <Badge status={s.status} />
                                    <span>{fmtReportDate(s.registered_at)}</span>
                                    <span>{fmtReportDate(s.subscription_date)}</span>
                                    <span>{s.last_payment_at ? fmtReportDate(s.last_payment_at) : '—'}</span>
                                    <span>{fmtReportDate(s.next_due_date)}</span>
                                    <span>{s.cancelled_at ? fmtReportDate(s.cancelled_at) : '—'}</span>
                                    <span>{s.has_addon ? 'Sim' : 'Não'}</span>
                                    <span className="text-slate-600 dark:text-slate-400 break-words" title={fmtReportCouponCell(s.coupon_codes, s.coupon_discount_sum_brl)}>
                                        {fmtReportCouponCell(s.coupon_codes, s.coupon_discount_sum_brl)}
                                    </span>
                                    <span className="text-slate-600 dark:text-slate-400 break-words" title={(s.coupon_moodle_student_names || '').trim() || ''}>
                                        {(s.coupon_moodle_student_names || '').trim() || '—'}
                                    </span>
                                    <span className="font-medium">{fmtReportMoney(s.total_paid_brl)}</span>
                                    <span className="text-slate-500">{fmtReportMoney(s.addon_paid_brl)}</span>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="flex flex-wrap justify-between items-center gap-2 mt-4 text-sm text-slate-600 dark:text-slate-400">
                        <span>
                            {subState.total} registro(s) — página {pageSub} de {totalPagesSub}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={pageSub <= 1 || subState.loading}
                                onClick={() => setPageSub((p) => Math.max(1, p - 1))}
                                className="px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 border-none cursor-pointer disabled:opacity-40"
                            >
                                Anterior
                            </button>
                            <button
                                type="button"
                                disabled={pageSub >= totalPagesSub || subState.loading}
                                onClick={() => setPageSub((p) => p + 1)}
                                className="px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 border-none cursor-pointer disabled:opacity-40"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                </>
            )}

            {tab === 'addons' && (
                <>
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                        <div
                            className="grid gap-2 p-3 px-4 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide min-w-[900px]"
                            style={{ gridTemplateColumns: 'minmax(120px,1.1fr) minmax(72px,0.65fr) minmax(140px,1fr) 88px minmax(88px,0.8fr) minmax(88px,0.8fr) minmax(88px,0.8fr) 88px' }}
                        >
                            <span>Cliente</span>
                            <span>Slug</span>
                            <span>Add-on</span>
                            <span>Ciclo</span>
                            <span>Aquisição</span>
                            <span>Próx. venc.</span>
                            <span>Cancel.</span>
                            <span>Status</span>
                        </div>
                        {addonState.loading ? (
                            <div className="p-8 text-center text-slate-400">Carregando…</div>
                        ) : addonState.rows.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 dark:text-slate-500">Nenhum add-on.</div>
                        ) : (
                            addonState.rows.map((s) => (
                                <div
                                    key={s.id}
                                    className="grid gap-2 p-3 px-4 border-b border-slate-100 dark:border-slate-700 text-xs items-center min-w-[900px] text-slate-700 dark:text-slate-300"
                                    style={{ gridTemplateColumns: 'minmax(120px,1.1fr) minmax(72px,0.65fr) minmax(140px,1fr) 88px minmax(88px,0.8fr) minmax(88px,0.8fr) minmax(88px,0.8fr) 88px' }}
                                >
                                    <span className="font-semibold text-slate-800 dark:text-slate-200">{s.tenant}</span>
                                    <span className="text-slate-500 truncate" title={s.slug}>{s.slug}</span>
                                    <span className="truncate" title={s.addon_name}>{s.addon_name}</span>
                                    <span className="text-slate-500">{s.billing_cycle === 'annual' ? 'Anual' : s.billing_cycle === 'semiannual' ? 'Semestral' : 'Mensal'}</span>
                                    <span>{fmtReportDate(s.acquired_at)}</span>
                                    <span>{fmtReportDate(s.next_due_date)}</span>
                                    <span>{s.cancelled_at ? fmtReportDate(s.cancelled_at) : '—'}</span>
                                    <Badge status={s.status === 'pending_checkout' ? 'pending' : s.status} />
                                </div>
                            ))
                        )}
                    </div>
                    <div className="flex flex-wrap justify-between items-center gap-2 mt-4 text-sm text-slate-600 dark:text-slate-400">
                        <span>
                            {addonState.total} registro(s) — página {pageAddon} de {totalPagesAddon}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={pageAddon <= 1 || addonState.loading}
                                onClick={() => setPageAddon((p) => Math.max(1, p - 1))}
                                className="px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 border-none cursor-pointer disabled:opacity-40"
                            >
                                Anterior
                            </button>
                            <button
                                type="button"
                                disabled={pageAddon >= totalPagesAddon || addonState.loading}
                                onClick={() => setPageAddon((p) => p + 1)}
                                className="px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 border-none cursor-pointer disabled:opacity-40"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ─── TEMPLATES (admin cria) ────────────────────────────────────
const TemplatesView = () => {
    const { t } = useTranslation(['master']);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: '', description: '', jsonInput: '' });
    const [creatingFrom, setCreatingFrom] = useState(null);
    const [exportJson, setExportJson] = useState(null);
    const [templateFeedback, setTemplateFeedback] = useState('');
    const [highlightTemplateId, setHighlightTemplateId] = useState(null);
    const highlightClearTimerRef = useRef(null);

    const load = useCallback((opts) => {
        const soft = opts?.soft === true;
        return fetch(`${API}/master/templates`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => setTemplates(d.templates || []))
            .finally(() => {
                if (!soft) setLoading(false);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => () => {
        if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current);
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!creatingFrom) return;
        const schema = typeof creatingFrom.schema_json === 'string' ? creatingFrom.schema_json : JSON.stringify(creatingFrom.schema_json);
        const r = await fetch(`${API}/master/templates`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ name: form.name, description: form.description, schema_json: schema })
        });
        const d = await r.json();
        if (r.ok) {
            const newId = d.id;
            setCreatingFrom(null);
            setForm({ name: '', description: '', jsonInput: '' });
            setTemplateFeedback(t('master:templateCreatedOk'));
            if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current);
            setHighlightTemplateId(newId || null);
            await load({ soft: true });
            if (newId) {
                requestAnimationFrame(() => {
                    const el = document.getElementById(`master-template-${newId}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
            highlightClearTimerRef.current = setTimeout(() => {
                setHighlightTemplateId(null);
                highlightClearTimerRef.current = null;
            }, 5000);
            window.setTimeout(() => setTemplateFeedback(''), 6500);
        } else {
            alert('❌ ' + (d.error || 'Erro ao criar template.'));
        }
    };

    const toggleActive = async (t) => {
        await fetch(`${API}/master/templates/${t.id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ is_active: t.is_active ? 0 : 1 }) });
        load();
    };

    const handleDelete = async (t) => {
        if (!confirm(`Excluir template "${t.name}"?`)) return;
        await fetch(`${API}/master/templates/${t.id}`, { method: 'DELETE', headers: authHeaders() });
        load();
    };

    if (loading) return <div className="p-8 text-slate-500 dark:text-slate-400">⏳ Carregando...</div>;

    return (
        <div>
            <h2 className="m-0 mb-6 text-slate-800 dark:text-slate-200">📄 Templates</h2>
            <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                Crie templates prontos para os clientes iniciarem projetos. Eles aparecerão no painel "Templates" do builder.
            </p>

            {templateFeedback ? (
                <div
                    className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
                    role="status"
                >
                    {templateFeedback}
                </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {templates.map((tpl) => (
                    <div
                        key={tpl.id}
                        id={`master-template-${tpl.id}`}
                        className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-2 transition-[box-shadow,ring] duration-300 ${
                            highlightTemplateId === tpl.id
                                ? 'border-emerald-500 ring-4 ring-emerald-400/70 dark:border-emerald-400 dark:ring-emerald-500/50'
                                : tpl.is_active
                                  ? 'border-blue-500 dark:border-blue-500'
                                  : 'border-slate-200 dark:border-slate-600'
                        }`}
                    >
                        <div className="font-bold text-slate-800 dark:text-slate-200 mb-1">{tpl.name}</div>
                        {tpl.description && <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">{tpl.description.slice(0, 60)}...</div>}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    try {
                                        setExportJson({ name: tpl.name, text: normalizeLayoutSchemaForJsonExport(tpl.schema_json) });
                                    } catch {
                                        alert(t('master:templateJsonInvalidStored'));
                                    }
                                }}
                                className="px-2.5 py-1 rounded-md bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 border-none cursor-pointer text-xs font-semibold"
                            >
                                {t('master:exportTemplateJson')}
                            </button>
                            <button type="button" onClick={() => toggleActive(tpl)} className={`px-2.5 py-1 rounded-md border-none cursor-pointer text-xs font-semibold ${tpl.is_active ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200' : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'}`}>
                                {tpl.is_active ? '⏸ Desativar' : '▶ Ativar'}
                            </button>
                            <button type="button" onClick={() => handleDelete(tpl)} className="px-2.5 py-1 rounded-md bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-none cursor-pointer text-xs font-semibold">🗑️</button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-600">
                <h4 className="m-0 mb-2 text-slate-700 dark:text-slate-300">{t('master:createTemplateFromJson')}</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    {t('master:createTemplateHint')}
                </p>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    try {
                        const parsed = JSON.parse(form.jsonInput || '{}');
                        setCreatingFrom({ schema_json: JSON.stringify(parsed) });
                    } catch (err) {
                        alert(t('master:invalidJson'));
                    }
                }} className="flex flex-col gap-2">
                    <textarea placeholder={t('master:createTemplateJsonPlaceholder')} value={form.jsonInput || ''} onChange={e => setForm(f => ({ ...f, jsonInput: e.target.value }))}
                        className="min-h-[80px] p-2 border border-slate-200 dark:border-slate-600 rounded-md text-xs font-mono text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800" />
                    <button type="submit" className="self-start px-4 py-2 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-semibold">{t('master:continue')}</button>
                </form>
            </div>

            <TemplateJsonExportModal
                open={!!exportJson}
                onClose={() => setExportJson(null)}
                fileName={exportJson?.name}
                jsonText={exportJson?.text || ''}
                labels={{
                    title: t('master:exportTemplateJsonModalTitle'),
                    hint: t('master:exportTemplateJsonHint'),
                    copy: t('master:exportTemplateJsonCopy'),
                    close: t('master:cancel'),
                    copied: t('master:templateJsonCopied'),
                    copyFailed: t('master:templateJsonCopyError'),
                }}
            />

            {creatingFrom && (
                <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-[400px] w-[90%] shadow-xl">
                        <h3 className="m-0 mb-4 text-lg font-semibold text-slate-800 dark:text-slate-200">{t('master:newTemplate')}</h3>
                        <form onSubmit={handleCreate}>
                            <div className="mb-4">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">{t('master:name')} *</label>
                                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('master:templateNamePlaceholder')}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800" />
                            </div>
                            <div className="mb-4">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">{t('master:description')}</label>
                                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('master:descriptionPlaceholder')}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800" />
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button type="button" onClick={() => setCreatingFrom(null)} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-none rounded-lg cursor-pointer font-semibold">{t('master:cancel')}</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-semibold">{t('master:createTemplate')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── GALERIA (aprovação de envios dos tenants) ─────────────────
const galleryRowStatus = (i) => {
    const s = String(i?.status ?? '').trim().toLowerCase();
    if (s === 'pending' || s === 'approved' || s === 'rejected') return s;
    if (i?.status === 1) return 'pending';
    if (i?.status === 2) return 'approved';
    if (i?.status === 3) return 'rejected';
    return s || 'pending';
};

const GalleryView = () => {
    const { t } = useTranslation(['master']);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editNameDraft, setEditNameDraft] = useState('');

    const load = () => {
        setLoading(true);
        setLoadError(null);
        fetch(`${API}/master/gallery`, { headers: authHeaders(), cache: 'no-store' })
            .then(async (r) => {
                const d = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setItems([]);
                    setLoadError(d.error || `Erro ${r.status} ao carregar a galeria. Confira o token Master e a URL da API (VITE_API_URL).`);
                    return;
                }
                setItems(Array.isArray(d.items) ? d.items : []);
            })
            .catch(() => {
                setItems([]);
                setLoadError('Falha de rede ao carregar a galeria.');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        await fetch(`${API}/master/gallery/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }) });
        load();
    };

    const setGalleryOfficial = async (id, official) => {
        const r = await fetch(`${API}/master/gallery/${id}`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ official }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
            alert(d.error || t('master:galleryOfficialError'));
            load();
            return;
        }
        load();
    };

    const saveGalleryName = async (id) => {
        const n = editNameDraft.trim();
        if (!n) {
            alert(t('master:galleryNameEmpty'));
            return;
        }
        const r = await fetch(`${API}/master/gallery/${id}`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ name: n }),
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
            setEditingId(null);
            setEditNameDraft('');
            load();
        } else {
            alert(d.error || t('master:galleryNameSaveError'));
        }
    };

    if (loading) return <div className="p-8 text-slate-500 dark:text-slate-400">⏳ Carregando...</div>;

    const pending = items.filter(i => galleryRowStatus(i) === 'pending');
    const approved = items.filter(i => galleryRowStatus(i) === 'approved');
    const rejected = items.filter(i => galleryRowStatus(i) === 'rejected');

    return (
        <div>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h2 className="m-0 mb-1 text-slate-800 dark:text-slate-200">🖼️ Galeria — Aprovações</h2>
                    <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
                        {t('master:galleryIntroBody')}
                    </p>
                </div>
                <button type="button" onClick={load} className="px-3 py-2 text-sm font-semibold rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-none cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600">
                    Atualizar lista
                </button>
            </div>

            {loadError && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
                    {loadError}
                </div>
            )}

            {!loadError && items.length === 0 && (
                <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                    Nenhum envio na fila. Se um cliente acabou de enviar, clique em «Atualizar lista» ou reabra esta aba. O envio e o Master precisam usar a <strong className="font-semibold">mesma API</strong> (mesmo <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">VITE_API_URL</code> / backend).
                </p>
            )}

            {pending.length > 0 && (
                <div className="mb-8">
                    <h4 className="m-0 mb-4 text-amber-600 dark:text-amber-400">⏳ Aguardando aprovação ({pending.length})</h4>
                    <div className="grid gap-4">
                        {pending.map(i => (
                            <div key={i.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 px-5 shadow-sm flex items-start justify-between flex-wrap gap-4 border border-slate-200 dark:border-slate-700">
                                <div className="flex-1 min-w-[220px]">
                                    {editingId === i.id ? (
                                        <div className="flex flex-col gap-2">
                                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">{t('master:galleryEditNameLabel')}</label>
                                            <input
                                                value={editNameDraft}
                                                onChange={(e) => setEditNameDraft(e.target.value)}
                                                maxLength={200}
                                                className="w-full max-w-md px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                            />
                                            <div className="flex flex-wrap gap-2">
                                                <button type="button" onClick={() => saveGalleryName(i.id)} className="px-3 py-1.5 bg-blue-600 text-white border-none rounded-lg cursor-pointer text-sm font-semibold">{t('master:gallerySaveName')}</button>
                                                <button type="button" onClick={() => { setEditingId(null); setEditNameDraft(''); }} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 border-none rounded-lg cursor-pointer text-sm font-semibold">{t('master:galleryCancelEdit')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="font-bold text-slate-800 dark:text-slate-200">{i.name}</div>
                                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                                {t('master:galleryPendingByTenant', { tenant: i.tenant_name || i.tenant_id || '—' })}
                                                {i.designer_name ? t('master:galleryPendingDesigner', { name: i.designer_name }) : ''}
                                                {' · '}
                                                {new Date(i.created_at).toLocaleString('pt-BR')}
                                            </div>
                                            {i.description && <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">{i.description}</div>}
                                        </>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {editingId !== i.id && (
                                        <>
                                            <button type="button" onClick={() => { setEditingId(i.id); setEditNameDraft(i.name || ''); }} className="px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-none rounded-lg cursor-pointer text-sm font-semibold">{t('master:galleryRename')}</button>
                                            <button type="button" onClick={() => setStatus(i.id, 'approved')} className="px-4 py-2 bg-emerald-600 text-white border-none rounded-lg cursor-pointer font-semibold">✅ Aprovar</button>
                                            <button type="button" onClick={() => setStatus(i.id, 'rejected')} className="px-4 py-2 bg-red-600 text-white border-none rounded-lg cursor-pointer font-semibold">❌ Rejeitar</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <h4 className="m-0 mb-4 text-emerald-600 dark:text-emerald-400">✅ Aprovados ({approved.length})</h4>
                    {approved.length === 0 ? <div className="text-slate-400 dark:text-slate-500 text-sm">Nenhum.</div> : (
                        <div className="flex flex-col gap-3">
                            {approved.map(i => (
                                <div
                                    key={i.id}
                                    className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800/40 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <div className="font-semibold text-emerald-900 dark:text-emerald-100">{i.name}</div>
                                        <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{i.tenant_name}</div>
                                    </div>
                                    <label className="flex items-start gap-2 cursor-pointer shrink-0 max-w-md">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                            checked={!!i.official_template_id}
                                            onChange={(e) => setGalleryOfficial(i.id, e.target.checked)}
                                        />
                                        <span className="text-xs text-slate-700 dark:text-slate-200 leading-snug">
                                            <span className="font-semibold block">{t('master:galleryOfficialCheckbox')}</span>
                                            <span className="text-slate-500 dark:text-slate-400">{t('master:galleryOfficialHint')}</span>
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <h4 className="m-0 mb-4 text-red-600 dark:text-red-400">❌ Rejeitados ({rejected.length})</h4>
                    {rejected.length === 0 ? <div className="text-slate-400 dark:text-slate-500 text-sm">Nenhum.</div> : (
                        <div className="flex flex-col gap-2">
                            {rejected.map(i => (
                                <div key={i.id} className="bg-red-50 dark:bg-red-900/20 p-2.5 px-3.5 rounded-lg flex justify-between items-center">
                                    <span className="font-semibold text-red-800 dark:text-red-200">{i.name}</span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">{i.tenant_name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── LOGS DE PLANOS ───────────────────────────────────────────
const PlanLogsView = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/master/plan-logs`, { headers: authHeaders() })
            .then(r => r.json())
            .then(d => { setLogs(d.logs || []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const parsePayload = (raw) => {
        try {
            return JSON.parse(raw || '{}');
        } catch {
            return {};
        }
    };

    if (loading) {
        return <div style={{ padding: '2rem', color: '#64748b' }}>⏳ Carregando logs...</div>;
    }

    return (
        <div>
            <h2 style={{ margin: '0 0 1.5rem', color: '#0f172a' }}>🧾 Logs de Planos</h2>
            {logs.length === 0 ? (
                <div style={{ padding: '2rem', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,.06)', color: '#94a3b8' }}>
                    Nenhum log de planos registrado ainda.
                </div>
            ) : (
                <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
                    <div style={{ padding: '0.8rem 1.2rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '180px 140px 1fr', gap: '1rem', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <span>Data/Hora</span>
                        <span>Tipo</span>
                        <span>Detalhes</span>
                    </div>
                    {logs.map(l => {
                        const p = parsePayload(l.payload);
                        return (
                            <div key={l.id} style={{ padding: '0.8rem 1.2rem', borderBottom: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: '180px 140px 1fr', gap: '1rem', fontSize: '0.85rem', alignItems: 'flex-start' }}>
                                <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                    {new Date(l.created_at).toLocaleString('pt-BR')}
                                </span>
                                <span style={{ fontWeight: 600, color: '#334155' }}>
                                    {l.event_type === 'plan_created' ? 'Criado' : 'Atualizado'}
                                </span>
                                <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                                    <div><strong>ID:</strong> {p.planId || '—'}</div>
                                    <div><strong>Nome:</strong> {p.name || '—'}</div>
                                    {'features' in p && (
                                        <div style={{ marginTop: '4px' }}>
                                            <strong>Features:</strong>{' '}
                                            <code style={{ fontSize: '0.78rem', background: '#f1f5f9', padding: '2px 4px', borderRadius: '4px' }}>
                                                {JSON.stringify(p.features)}
                                            </code>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── CONFIGURAÇÕES (reCAPTCHA e outras) ───────────────────────
const SettingsView = () => {
    const { t } = useTranslation(['master']);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
    const [recaptchaSecretKey, setRecaptchaSecretKey] = useState('');
    const [termsOfUse, setTermsOfUse] = useState('');
    const [privacyPolicy, setPrivacyPolicy] = useState('');
    const [planUiAnnualPt, setPlanUiAnnualPt] = useState('');
    const [planUiAnnualEn, setPlanUiAnnualEn] = useState('');
    const [planUiBadgePt, setPlanUiBadgePt] = useState('');
    const [planUiBadgeEn, setPlanUiBadgeEn] = useState('');
    const [planCorporateContactUrl, setPlanCorporateContactUrl] = useState('');
    const [openaiConfigured, setOpenaiConfigured] = useState(false);
    const [openaiHint, setOpenaiHint] = useState('');
    const [openaiApiKey, setOpenaiApiKey] = useState('');
    const [openaiModel, setOpenaiModel] = useState('gpt-4o-mini');
    const [openaiClearKey, setOpenaiClearKey] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${API}/master/settings`, { headers: authHeaders() })
            .then(r => r.json())
            .then(d => {
                setRecaptchaSiteKey(d.recaptcha_site_key || '');
                setRecaptchaSecretKey(d.recaptcha_secret_key || '');
                setTermsOfUse(d.terms_of_use || '');
                setPrivacyPolicy(d.privacy_policy || '');
                setPlanUiAnnualPt(d.plan_ui_annual_pt || '');
                setPlanUiAnnualEn(d.plan_ui_annual_en || '');
                setPlanUiBadgePt(d.plan_ui_save_badge_pt || '');
                setPlanUiBadgeEn(d.plan_ui_save_badge_en || '');
                setPlanCorporateContactUrl(d.plan_corporate_contact_url || '');
                setOpenaiConfigured(!!d.openai_configured);
                setOpenaiHint(d.openai_api_key_hint || '');
                setOpenaiModel(d.openai_model || 'gpt-4o-mini');
                setOpenaiApiKey('');
                setOpenaiClearKey(false);
            })
            .catch(() => setMsg(t('master:settingsLoadError')))
            .finally(() => setLoading(false));
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true); setMsg('');
        try {
            const body = {
                recaptcha_site_key: recaptchaSiteKey.trim(),
                recaptcha_secret_key: recaptchaSecretKey.trim(),
                terms_of_use: termsOfUse,
                privacy_policy: privacyPolicy,
                plan_ui_annual_pt: planUiAnnualPt.trim(),
                plan_ui_annual_en: planUiAnnualEn.trim(),
                plan_ui_save_badge_pt: planUiBadgePt.trim(),
                plan_ui_save_badge_en: planUiBadgeEn.trim(),
                plan_corporate_contact_url: planCorporateContactUrl.trim(),
                openai_model: openaiModel.trim() || 'gpt-4o-mini',
            };
            if (openaiClearKey) {
                body.openai_clear_key = true;
            } else if (openaiApiKey.trim()) {
                body.openai_api_key = openaiApiKey.trim();
            }
            const r = await fetch(`${API}/master/settings`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify(body),
            });
            const d = await r.json();
            if (r.ok) {
                setMsg(t('master:settingsSavedOk'));
                setOpenaiApiKey('');
                setOpenaiClearKey(false);
                load();
            } else {
                setMsg(t('master:settingsSavedErr') + (d.error ? `: ${d.error}` : ''));
            }
        } catch {
            setMsg(t('master:settingsNetworkErr'));
        }
        setSaving(false);
    };

    if (loading) return <div className="p-8 text-slate-500 dark:text-slate-400">⏳ {t('master:loading')}</div>;

    const inputCls = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800';
    const labelCls = 'block text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1';

    return (
        <div>
            <h2 className="m-0 mb-6 text-slate-800 dark:text-slate-200">⚙️ {t('master:settingsPageTitle')}</h2>
            <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{t('master:settingsPageIntro')}</p>

            {msg && (
                <div className={`mb-6 p-4 rounded-lg font-semibold ${msg.startsWith('✅') ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'}`}>
                    {msg}
                </div>
            )}

            <form onSubmit={handleSave} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 max-w-4xl space-y-10">
                <section>
                    <h3 className="m-0 mb-4 text-slate-800 dark:text-slate-200">{t('master:settingsRecaptchaTitle')}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        {t('master:settingsRecaptchaIntro')}{' '}
                        <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">
                            {t('master:settingsRecaptchaLink')}
                        </a>
                    </p>
                    <div className="space-y-4">
                        <div>
                            <label className={labelCls}>{t('master:settingsSiteKey')}</label>
                            <input
                                type="text"
                                value={recaptchaSiteKey}
                                onChange={e => setRecaptchaSiteKey(e.target.value)}
                                className={inputCls}
                                placeholder="6Lc..."
                            />
                        </div>
                        <div>
                            <label className={labelCls}>{t('master:settingsSecretKey')}</label>
                            <input
                                type="password"
                                value={recaptchaSecretKey}
                                onChange={e => setRecaptchaSecretKey(e.target.value)}
                                className={inputCls}
                                placeholder="6Lc..."
                                autoComplete="off"
                            />
                        </div>
                    </div>
                </section>

                <section className="border-t border-slate-100 dark:border-slate-700 pt-10">
                    <h3 className="m-0 mb-2 text-slate-800 dark:text-slate-200">
                        {t('master:settingsOpenaiTitle', { defaultValue: 'OpenAI (Geração de layout IA)' })}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        {t('master:settingsOpenaiIntro', {
                            defaultValue: 'Chave usada pela conta Mestra para personalizar textos do layout (Fase 4). Armazenada criptografada. Deixe o campo de chave em branco para manter a atual.',
                        })}
                    </p>
                    <div className="space-y-4">
                        <div>
                            <label className={labelCls}>
                                {t('master:settingsOpenaiKey', { defaultValue: 'API Key OpenAI' })}
                                {openaiConfigured ? (
                                    <span className="ml-2 text-xs font-normal text-emerald-600 dark:text-emerald-400">
                                        {t('master:settingsOpenaiConfigured', {
                                            defaultValue: 'Configurada',
                                        })}
                                        {openaiHint ? ` (${openaiHint})` : ''}
                                    </span>
                                ) : (
                                    <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                                        {t('master:settingsOpenaiMissing', { defaultValue: 'Não configurada' })}
                                    </span>
                                )}
                            </label>
                            <input
                                type="password"
                                value={openaiApiKey}
                                onChange={(e) => { setOpenaiApiKey(e.target.value); setOpenaiClearKey(false); }}
                                className={inputCls}
                                placeholder={openaiConfigured ? '••••••••  (nova chave opcional)' : 'sk-...'}
                                autoComplete="off"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>
                                {t('master:settingsOpenaiModel', { defaultValue: 'Modelo' })}
                            </label>
                            <input
                                type="text"
                                value={openaiModel}
                                onChange={(e) => setOpenaiModel(e.target.value)}
                                className={inputCls}
                                placeholder="gpt-4o-mini"
                                autoComplete="off"
                            />
                            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 m-0">
                                {t('master:settingsOpenaiModelHint', {
                                    defaultValue: 'Ex.: gpt-4o-mini (barato) ou gpt-4o.',
                                })}
                            </p>
                        </div>
                        {openaiConfigured && (
                            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={openaiClearKey}
                                    onChange={(e) => setOpenaiClearKey(e.target.checked)}
                                />
                                {t('master:settingsOpenaiClear', { defaultValue: 'Remover chave salva' })}
                            </label>
                        )}
                    </div>
                </section>

                <section className="border-t border-slate-100 dark:border-slate-700 pt-10">
                    <h3 className="m-0 mb-2 text-slate-800 dark:text-slate-200">{t('master:settingsPlanUiTitle')}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('master:settingsPlanUiHint')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>{t('master:settingsPlanUiAnnualPt')}</label>
                            <input type="text" value={planUiAnnualPt} onChange={e => setPlanUiAnnualPt(e.target.value)} className={inputCls} placeholder="Pagamento anual" />
                        </div>
                        <div>
                            <label className={labelCls}>{t('master:settingsPlanUiAnnualEn')}</label>
                            <input type="text" value={planUiAnnualEn} onChange={e => setPlanUiAnnualEn(e.target.value)} className={inputCls} placeholder="Annual billing" />
                        </div>
                        <div>
                            <label className={labelCls}>{t('master:settingsPlanUiBadgePt')}</label>
                            <input type="text" value={planUiBadgePt} onChange={e => setPlanUiBadgePt(e.target.value)} className={inputCls} placeholder="Economize 20%" />
                        </div>
                        <div>
                            <label className={labelCls}>{t('master:settingsPlanUiBadgeEn')}</label>
                            <input type="text" value={planUiBadgeEn} onChange={e => setPlanUiBadgeEn(e.target.value)} className={inputCls} placeholder="Save 20%" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>{t('master:settingsCorporatePlanUrlLabel')}</label>
                            <input
                                type="url"
                                value={planCorporateContactUrl}
                                onChange={(e) => setPlanCorporateContactUrl(e.target.value)}
                                className={inputCls}
                                placeholder="https://seusite.com/contato-corporativo"
                                autoComplete="off"
                            />
                            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 m-0">{t('master:settingsCorporatePlanUrlHint')}</p>
                        </div>
                    </div>
                </section>

                <section className="border-t border-slate-100 dark:border-slate-700 pt-10">
                    <h3 className="m-0 mb-2 text-slate-800 dark:text-slate-200">{t('master:settingsLegalTitle')}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('master:settingsLegalHint')}</p>
                    <div className="space-y-6">
                        <div>
                            <label className={labelCls}>{t('master:settingsTermsLabel')}</label>
                            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden dark:border-slate-600 dark:bg-slate-800 [&_.ql-container]:min-h-[220px] [&_.ql-editor]:min-h-[200px] [&_.ql-toolbar]:border-slate-200 dark:[&_.ql-toolbar]:border-slate-600 dark:[&_.ql-toolbar]:bg-slate-700 [&_.ql-stroke]:stroke-slate-200">
                                <ReactQuill theme="snow" value={termsOfUse} onChange={setTermsOfUse} modules={LEGAL_QUILL_MODULES} placeholder={t('master:settingsTermsPlaceholder')} />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>{t('master:settingsPrivacyLabel')}</label>
                            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden dark:border-slate-600 dark:bg-slate-800 [&_.ql-container]:min-h-[220px] [&_.ql-editor]:min-h-[200px] [&_.ql-toolbar]:border-slate-200 dark:[&_.ql-toolbar]:border-slate-600 dark:[&_.ql-toolbar]:bg-slate-700 [&_.ql-stroke]:stroke-slate-200">
                                <ReactQuill theme="snow" value={privacyPolicy} onChange={setPrivacyPolicy} modules={LEGAL_QUILL_MODULES} placeholder={t('master:settingsPrivacyPlaceholder')} />
                            </div>
                        </div>
                    </div>
                </section>

                <div className="border-t border-slate-100 dark:border-slate-700 pt-6">
                    <button type="submit" disabled={saving}
                        className="px-6 py-2.5 bg-blue-600 text-white border-none rounded-lg cursor-pointer font-bold disabled:opacity-60 hover:bg-blue-700">
                        {saving ? t('master:settingsSaving') : t('master:settingsSaveAll')}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ─── PLUGINS MOODLE (ZIP + manifesto para clientes) ─────────────
function MoodlePluginsMasterView({ t }) {
    const [plugins, setPlugins] = useState([]);
    const [etag, setEtag] = useState('');
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${API}/master/moodle-plugins`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => {
                setPlugins(d.plugins || []);
                setEtag(d.manifest_etag || '');
            })
            .catch(() => setMsg(t('master:settingsNetworkErr')))
            .finally(() => setLoading(false));
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const updateField = (id, field, value) => {
        setPlugins((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    };

    const saveMeta = () => {
        setSaving(true);
        setMsg('');
        fetch(`${API}/master/moodle-plugins`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({
                plugins: plugins.map((p) => ({
                    id: p.id,
                    title: p.title,
                    subtitle: p.subtitle,
                    version: p.version,
                    updated_at: p.updated_at,
                    changelog: p.changelog,
                })),
            }),
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || 'Erro');
                setPlugins(d.plugins || []);
                if (d.manifest_etag) setEtag(d.manifest_etag);
                setMsg(t('master:moodlePluginsSaved'));
            })
            .catch((e) => setMsg(e.message || String(e)))
            .finally(() => setSaving(false));
    };

    const uploadZip = (pluginId, file) => {
        if (!file) return;
        setUploading(pluginId);
        setMsg('');
        const fd = new FormData();
        fd.append('file', file);
        fetch(`${API}/master/moodle-plugins/${pluginId}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${getToken()}` },
            body: fd,
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.error || 'Upload falhou');
                setMsg(t('master:moodlePluginsUploadOk', { id: pluginId }));
                load();
            })
            .catch((e) => setMsg(e.message || String(e)))
            .finally(() => setUploading(null));
    };

    const mplabel = 'block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1';
    const mpinput = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200';

    if (loading && plugins.length === 0) {
        return <div className="p-8 text-slate-500 dark:text-slate-400">⏳ {t('master:loading')}</div>;
    }

    return (
        <div className="max-w-4xl">
            <h2 className="m-0 mb-2 text-slate-800 dark:text-slate-200">{t('master:moodlePluginsTitle')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('master:moodlePluginsIntro')}</p>
            {etag ? (
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                    {t('master:moodlePluginsEtagHint')}: <span className="font-mono text-slate-600 dark:text-slate-300">{etag}</span>
                </p>
            ) : null}
            {msg ? (
                <div className="mb-4 text-sm text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600">
                    {msg}
                </div>
            ) : null}
            <div className="space-y-8">
                {plugins.map((p) => (
                    <div key={p.id} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                        <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-4 font-mono">{p.id}</h3>
                        <div className="grid gap-3 mb-4">
                            <div>
                                <label className={mplabel}>{t('master:moodlePluginsTitleLabel')}</label>
                                <input className={mpinput} value={p.title || ''} onChange={(e) => updateField(p.id, 'title', e.target.value)} />
                            </div>
                            <div>
                                <label className={mplabel}>{t('master:moodlePluginsSubtitleLabel')}</label>
                                <input className={mpinput} value={p.subtitle || ''} onChange={(e) => updateField(p.id, 'subtitle', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className={mplabel}>{t('master:moodlePluginsVersionLabel')}</label>
                                    <input className={mpinput} value={p.version || ''} onChange={(e) => updateField(p.id, 'version', e.target.value)} />
                                </div>
                                <div>
                                    <label className={mplabel}>{t('master:moodlePluginsDateLabel')}</label>
                                    <input className={mpinput} value={p.updated_at || ''} onChange={(e) => updateField(p.id, 'updated_at', e.target.value)} placeholder="2026-03-31" />
                                </div>
                            </div>
                            <div>
                                <label className={mplabel}>{t('master:moodlePluginsChangelogLabel')}</label>
                                <textarea className={`${mpinput} min-h-[120px] font-mono text-xs`} value={p.changelog || ''} onChange={(e) => updateField(p.id, 'changelog', e.target.value)} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 items-stretch sm:items-start border-t border-slate-100 dark:border-slate-700 pt-4">
                            <label className="inline-flex justify-center sm:justify-start items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold cursor-pointer hover:bg-blue-700 w-full sm:w-auto">
                                <input
                                    type="file"
                                    accept=".zip,application/zip"
                                    className="hidden"
                                    onChange={(e) => {
                                        uploadZip(p.id, e.target.files?.[0]);
                                        e.target.value = '';
                                    }}
                                />
                                {uploading === p.id ? '…' : t('master:moodlePluginsUploadZip')}
                            </label>
                            <span className={`text-xs font-medium ${p.has_file ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                {p.has_file ? t('master:moodlePluginsHasZip') : t('master:moodlePluginsNoZip')}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            <button
                type="button"
                disabled={saving}
                onClick={saveMeta}
                className="mt-8 px-6 py-2.5 bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 rounded-lg font-bold disabled:opacity-60 hover:opacity-90"
            >
                {saving ? '…' : t('master:moodlePluginsSaveMeta')}
            </button>
        </div>
    );
}

// ─── MAIN MASTER DASHBOARD ────────────────────────────────────
export default function MasterDashboard({ user, onLogout, onLoginAsTenant }) {
    const { t } = useTranslation(['master']);
    const [view, setView] = useState('dashboard');
    const [plans, setPlans] = useState([]);

    const loadPlans = useCallback(() => {
        fetch(`${API}/master/plans`, { headers: authHeaders() })
            .then(r => r.json()).then(d => setPlans(d.plans || []));
    }, []);

    useEffect(() => { loadPlans(); }, [loadPlans]);

    const nav = [
        { id: 'dashboard', label: '📊 Dashboard' },
        { id: 'clients', label: '🏢 Clientes' },
        { id: 'plans', label: '💎 Planos' },
        { id: 'templates', label: '📄 Templates' },
        { id: 'gallery', label: '🖼️ Galeria' },
        { id: 'leadlovers', label: '📧 Leadlovers' },
        { id: 'admins', label: '🛡️ Administradores' },
        { id: 'reports', label: '📈 Relatórios' },
        { id: 'feedback', label: '📝 Feedback' },
        { id: 'planlogs', label: '🧾 Logs de Planos' },
        { id: 'settings', label: '⚙️ Configurações' },
        { id: 'moodle_plugins', label: `🔌 ${t('master:moodlePluginsNav')}` },
    ];

    return (
        <div className="flex h-screen font-sans bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
            {/* SIDEBAR */}
            <div className="w-60 bg-[#001d4d] flex flex-col flex-shrink-0">
                <div className="p-6 pb-4 border-b border-white/10">
                    <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Geppetto Creator</div>
                    <div className="text-white font-extrabold text-base">⚡ Master Admin</div>
                    <div className="text-sm text-white/50 mt-1">{user.email}</div>
                </div>

                <nav className="flex-1 p-4 py-2">
                    {nav.map(n => (
                        <button key={n.id} type="button" onClick={() => setView(n.id)} className={`block w-full text-left py-2.5 px-3.5 mb-0.5 rounded-lg cursor-pointer text-sm transition-all ${view === n.id ? 'bg-white/15 text-white font-bold' : 'bg-transparent text-white/65 font-normal hover:bg-white/5'}`}>
                            {n.label}
                        </button>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10">
                    <button type="button" onClick={onLogout} className="w-full py-2 bg-white/10 text-white/70 border-none rounded-lg cursor-pointer text-sm font-semibold hover:bg-white/15">
                        🚪 Sair
                    </button>
                </div>
            </div>

            {/* CONTEÚDO */}
            <div className="flex-1 overflow-auto p-8 bg-slate-50 dark:bg-slate-900">
                {view === 'dashboard' && <DashboardView t={t} />}
                {view === 'clients' && <ClientsView plans={plans} onLoginAsTenant={onLoginAsTenant} />}
                {view === 'plans' && <PlansArea plans={plans} onRefresh={loadPlans} t={t} />}
                {view === 'templates' && <TemplatesView />}
                {view === 'gallery' && <GalleryView />}
                {view === 'leadlovers' && <LeadloversIntegrationPanel />}
                {view === 'admins' && <AdminsView />}
                {view === 'reports' && <ReportsView />}
                {view === 'feedback' && <UserFeedbackMasterView />}
                {view === 'planlogs' && <PlanLogsView />}
                {view === 'settings' && <SettingsView />}
                {view === 'moodle_plugins' && <MoodlePluginsMasterView t={t} />}
            </div>
        </div>
    );
}
